// ModernLayout — Phase 10 β「CSP 入門 gateway」レイアウト (engine 共有・UI 全面新規).
//
// 確定設計: docs/_wip/modern-layout.md §9 (案B 骨格) / §9.1-9.4 / §9.6 方針転換
//   (consult 2026-06-01 / 2026-06-08)。
//
// ## §9.6 方針転換 (2026-06-08): engine 共有のみ・UI 全面新規
//
// 当初シェル方針「Classic の既存部品 (ColorWheelPalette / ArgbSliderPanel /
// LayerPopup / ToolPanel / PalettePanel / MaskPanel) をドックに再配置」を **廃止**し、
// **描画エンジン層だけ共有して UI を全面新規** にした (§9.6)。Classic 部品は import
// しない。CSP / 一般ペイント風のクリーンな新 UI を新規コンポーネントで構築する。
//
// 共有する層 (UI 非依存、そのまま駆動): CanvasDrawingEngine / RoomState / WS プロトコル
// / snapshot / layer モデル / zoom / imagepaste / 認証・member。これらは `useRoomChrome`
// (Modern 専用の engine controller として継続進化) と `useSession` 越しに駆動する。
//
// engine ブリッジ / canvas オーバーレイ (DrawingCanvas / BrushCursorOverlay /
// ImagepasteOverlay / NameLabelOverlay / PreviewOverlay) は Classic paint UI でなく
// engine 接続部なので再利用 OK (§9.6 共有する/残す層)。
//
// ## 骨格 (§9 案B → 配置調整 2026-06-08)
//   ┌──┬─────────────────┬──────────┐
//   │T │                 │ Preview  │  左:   ツール帯 + コマンド + ツールプロパティ + カラー
//   │o │                 │ Layers   │  中:   canvas 最大化 (右独立列を畳んだ分さらに広がる)
//   │o │   Canvas        │ Members  │  中右: プレビュー → レイヤー → メンバー → チャット (1本のドック)
//   │l │   (最大化)      │ Chat     │
//   └──┴─────────────────┴──────────┘
//
// §9 案B の右端チャット専用ドロワー (独立列) は使い勝手調整で中右ドックに統合
// (consult 2026-06-08 ユーザ指示)。横幅を取りすぎる 300px ドロワーを廃し、メンバー /
// チャットをドックに畳んで canvas を広げた。チャットは flex-1 で残り高さを占有する。
//
// ## engine 永続化 (§9.7 Phase 1、2026-06-08): canvas 本体は持たない
// レイアウト切替で canvas をクリア/desync させないため、engine/canvas/overlay/描画状態
// store を切替の上位 (App.tsx RoomLayout) に hoist した。ModernLayout は **chrome ビュー**
// (パネル・ツール帯・チャット) だけを担い、canvas 領域は **空スロット div** にする
// (= CanvasStage が RoomLayout 直下 absolute でこの矩形に重なる)。
//   - 描画状態 store (useRoomChrome) は RoomLayout が所有し props.chrome で受け取る。
//   - DrawingCanvas / NameLabel / BrushCursor / Imagepaste / マスク枠は CanvasStage へ移設。
//   - canvas scroll の anchor (scrollRef) と空スロット ref (canvasSlotRef) は RoomLayout が
//     所有し props で渡す。プレビューパネルは scrollRef + chrome.canvasRef を読む。
//
// ## 描画能力は増やさない (§9.6 スコープ注記)
// 図形 / カスタムブラシ詳細 / 手ぶれ補正 / layout スタンプ / リッチレイヤー
// (表示濃度・ブレンド・追加削除並べ替え) は別スライス。新規プロトコル・engine 改変なし。

import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type BellOnState,
  type BellSoundAdapter,
  createBellOnRoominStore,
  createBrowserBellSound,
} from '../../../application/bellOnRoomin.js';
import type { RoomState } from '../../../domain/RoomState.js';
import {
  type ChatFontScale,
  chatFontScaleToEm,
  createChatFontScalePreferenceStore,
} from '../../../infrastructure/chatFontScalePreference.js';
import { createCustomCursorNewBadgeSeenStore } from '../../../infrastructure/customCursorNewBadgeSeen.js';
import { ZOOM_FIT_MIN, ZOOM_MAX } from '../../../infrastructure/drawing/CanvasDrawingEngine.js';
// 使い方ボタンの「New!」バッジ seen フラグ (2026-09-22 追補、目立たせ改修)。
import { createHelpButtonSeenStore } from '../../../infrastructure/helpButtonSeen.js';
import { createLayoutOnboardingSeenStore } from '../../../infrastructure/layoutOnboardingSeen.js';
import type { LayoutKind } from '../../../infrastructure/layoutPreference.js';
import { createModernToolPropertiesDetailsPreferenceStore } from '../../../infrastructure/modernToolPropertiesDetailsPreference.js';
import { createModernWorkspacePreferenceStore } from '../../../infrastructure/modernWorkspacePreference.js';
// 過去キャンバス入口の「New!」バッジ seen フラグ (Slice 2、絵ログとは別 key)。
import { createPastCanvasSeenStore } from '../../../infrastructure/pastCanvasSeen.js';
// カーソル入口の「New!」バッジ seen フラグ (2026-09-19 追補、絵ログ/過去キャンバスとは別 key)。
import { createRotateNewBadgeSeenStore } from '../../../infrastructure/rotateNewBadgeSeen.js';
// T3 タイムラプス共有: `?worker` import (timelapseWorkerFactory) はここで解決して
// モーダルへ注入する (= TimelapseShareModal を node テスト可能に保つ、T2 と同方針)。
import { createTimelapseWorker } from '../../../infrastructure/timelapse/worker/timelapseWorkerFactory.js';
// 絵ログ共有ボタンの「New!」バッジ seen フラグ (consult 2026-07-08 案B): 初回に
// モーダルを開いたら消す。Classic / Modern 両入口で同じ store key を共有。
import { createTimelapseShareSeenStore } from '../../../infrastructure/timelapseShareSeen.js';
import {
  createUiScalePreferenceStore,
  type UiScale,
  uiScaleToCssZoom,
} from '../../../infrastructure/uiScalePreference.js';
import { createWidthPresetPreferenceStore } from '../../../infrastructure/widthPresetPreference.js';
import { ConfirmDialog } from '../../ConfirmDialog.js';
import { FeedbackButton } from '../../FeedbackButton.js';
import { TIMELAPSE_SHARE_ENABLED } from '../../featureFlags.js';
import { UNDO_REJECT_DISPLAY_MS, useConflictAwareUndo } from '../../hooks/useConflictAwareUndo.js';
import { useInviteDialog } from '../../hooks/useInviteDialog.js';
import { useSession } from '../../hooks/useSession.js';
import { useTimelapseExportSupported } from '../../hooks/useTimelapseExportSupported.js';
import { useWhatsNew } from '../../hooks/useWhatsNew.js';
import { InviteDialog } from '../../InviteDialog.js';
import { buildHelpUrl } from '../../manual/helpUrl.js';
import { PastCanvasListModal } from '../../PastCanvasListModal.js';
import { PenSettingsModal } from '../../PenSettingsModal.js';
import { RoomSettingsModal } from '../../RoomSettingsModal.js';
import { ShortcutSettingsModal } from '../../ShortcutSettingsModal.js';
import { TimelapseShareModal } from '../../TimelapseShareModal.js';
import { UserPreferencesModal } from '../../UserPreferencesModal.js';
import { buildRoomInviteUrl } from '../../urlNavigation.js';
import { WhatsNewDialog } from '../../WhatsNewDialog.js';
import type { ChatPanelHandle } from '../classic/ChatPanel.js';
// conflict-aware Undo (β Modern) の拒否トースト: imagepaste 拒否と同じ「キャンバス中央・
// 約 3 秒自動消滅」流儀を再利用する (undoRejectText.ts のコメント参照)。
import { ImagepasteToastOverlay } from '../classic/ImagepasteToastOverlay.js';
import { isLocalInteractionActive } from '../classic/previewOverlay.js';
// conflict-aware Undo の bbox ハイライト (undo-conflict-aware-2026-07-12.md「B' 実装
// での追加事項」)。ImagepasteOverlay/ImagepasteToastOverlay と同じ canvasRef 座標変換
// 流儀 — CanvasStage の外 (ModernLayout) からそのまま配線できる。
import { UndoConflictHighlightOverlay } from '../classic/UndoConflictHighlightOverlay.js';
import { LayoutToggle, shouldShowOnboardingCoachmark } from '../LayoutToggle.js';
import { markCursorEditorHintSeen, readCursorEditorHintSeen } from '../shared/customCursorHint.js';
import { FloatShell } from '../shared/FloatShell.js';
import {
  bringToFront,
  clampFloatPosition,
  clampGrabOffset,
  FLOAT_DETACH_PREVIEW_Z,
  FLOAT_EDGE_INSET_PX,
  FLOAT_GRAB_OFFSET,
  FLOAT_PANEL_FALLBACK_HEIGHT,
  type FloatPoint,
  type FloatSize,
  floatPanelSide,
  floatPositionFromPointer,
  sizeEquals,
  viewportSizeNow,
  zIndexFromOrder,
} from '../shared/floatShellGeometry.js';
import {
  DOCK_SNAP_MARGIN_PX,
  EDGE_SNAP_THRESHOLD_PX,
  type PanelDropCandidate,
  type RegionEndCandidate,
  resolveDockSnapTarget,
  resolveEdgeSnapSide,
  resolvePanelDropTarget,
} from '../shared/panelDragGeometry.js';
import { RoomHeader, type RoomHeaderItem } from '../shared/RoomHeader.js';
import { RoomRetentionChip } from '../shared/RoomRetentionChip.js';
import { useFullscreen } from '../shared/useFullscreen.js';
// §9.7 engine 永続化: 描画状態 store (useRoomChrome) は RoomLayout が所有し props で
// 受け取る。canvas 本体 (DrawingCanvas / overlay / Imagepaste / マスク枠) は CanvasStage
// へ移設したので ここでは import しない。
import type { RoomChrome } from '../shared/useRoomChrome.js';
import { useLayoutKeyboardShortcuts } from '../useLayoutKeyboardShortcuts.js';
import { useSpaceHoldPan } from '../useSpaceHoldPan.js';
import { ModernBrushPanel } from './ModernBrushPanel.js';
import { ModernBrushPanelFlat } from './ModernBrushPanelFlat.js';
import { ModernColorPalette } from './ModernColorPalette.js';
// Modern 専用の新規 UI 部品 (§9.6 全面新規)。
import { ModernColorPanel } from './ModernColorPanel.js';
import { ModernCommandCluster } from './ModernCommandCluster.js';
import { ModernCursorEditorPanel } from './ModernCursorEditorPanel.js';
import { ModernDockSplitter } from './ModernDockSplitter.js';
// 使い方ボタン (2026-09-22 目立たせ改修) の丸囲み「?」装飾アイコン。
import { HelpIcon, InviteIcon } from './ModernIcons.js';
import { ModernLayerPanel } from './ModernLayerPanel.js';
import { type DropTarget, ModernPanelDragShell } from './ModernPanelDragShell.js';
import { ModernPanelVisibilityMenu } from './ModernPanelVisibilityMenu.js';
// M1-b フォンシェル (modern-phone-shell-2026-07-02.md §3.1): スマホでは panelRegistry を
// 「全画面 canvas + 下部バー + ボトムシート」へ再ホストする別シェルに差し替える。
import { ModernPhoneShell } from './ModernPhoneShell.js';
import { ModernPreviewPanel } from './ModernPreviewPanel.js';
import { ModernSocialDock } from './ModernSocialDock.js';
import { ModernToolProperties } from './ModernToolProperties.js';
import { ModernToolRail } from './ModernToolRail.js';
import { ModernWorkspacePresetPicker } from './ModernWorkspacePresetPicker.js';
import {
  floatPanelLive,
  floatRailLive,
  type LiveWorkspace,
  liveFromPref,
  movePanelLive,
  moveRailLive,
  pickerValue,
  prefFromLive,
  resolveLive,
  selectPreset,
  setLiveAsideWidth,
  togglePanelHidden,
} from './modernWorkspaceLive.js';
import { useRecentColors } from './useRecentColors.js';
import {
  addWidthPreset,
  DEFAULT_WIDTH_PRESETS,
  removeWidthPreset,
  stepPenWidth,
} from './widthPresets.js';
import {
  asideWidthPx,
  currentRailSlot,
  edgeSnapRegion,
  homeRegionOf,
  isAsideEmpty,
  isPanelFloating,
  isPanelVisible,
  isRailFloating,
  isRailOnlyAside,
  isRailPanel,
  nextPresetId,
  type PanelId,
  type RailSlot,
  RIGHT_DOCK_WIDTH_DEFAULT,
  railSlotIndicatorOffset,
  railSlotNearPointer,
  splitAroundCanvas,
  TOOL_DOCK_WIDTH_DEFAULT,
  visiblePanels,
  WORKSPACE_PRESETS,
  type WorkspacePresetId,
  type WorkspaceRegion,
} from './workspaceLayout.js';

// 第5スライス (レール端ドック): leftRail region を構成するパネル群 (RAIL_PANEL_IDS /
// isRailPanel) は workspaceLayout から import する (= 純関数 movePanel/validate と同一の
// 単一ソース)。これらの grip ドラッグは panel 移動でなく **region 移動** (= レール全体を
// 4 スロットへ) のセマンティクスに分岐する。どちらの grip を掴んでもレール (= 2 パネルセット)
// ごと動く (consult 2026-06-17 確定)。

// 2026-06-13: Modern ヘッダ中央の「超テスト版」注意書き (= 共有 RoomHeader の notice
// slot へ渡す。Classic には出さない)。バグ報告の心理的ハードルを下げる温度感の文言。
// 文言を変えるときはこの定数だけ直す (⚠️ アイコンは RoomHeader 側が装飾として前置)。
export const MODERN_TEST_BUILD_NOTICE = '超テスト版につき、バグが多いかもしれません';

// S1 (floating-panels-2026-08-01.md §9): パネル DnD 確定直後、ゴーストクリック (タッチの
// 互換 click) を飲み込む猶予時間 (ms)。armGhostClickGuard 参照。
const GHOST_CLICK_GUARD_MS = 400;

// S2 (floating-panels-2026-08-01.md §6): フロートパネルの既定幅 (px)。home region (=
// `homeRegionOf` が指す DEFAULT_WORKSPACE 上の所属 region) のドック既定幅をそのまま流用する
// (= ドック時と極端に幅が変わらない、ツール系 244px / 社交・レイヤー・プレビュー系 260px)。
// FloatShell の style width にもそのまま使う (純関数、component 外に置ける)。
const floatWidthFor = (id: PanelId): number =>
  homeRegionOf(id) === 'rightDock' ? RIGHT_DOCK_WIDTH_DEFAULT : TOOL_DOCK_WIDTH_DEFAULT;

// §12 (floating-panels-2026-08-01.md、方針変更 2026-08-01): leftRail (toolRail+
// commandCluster 一体) がフロートするとき、z-order (`floatOrder`) / 実測サイズ
// (`floatSizes`) の lookup キーとして使う代表 id。`toolRail`/`commandCluster` の
// どちらの grip を掴んでもレール全体が動く (= 1 ユニット) ので、実際に掴んだ id が
// どちらであっても常にこのキーへ正規化する (= 判定・計測を二重に持たない)。
const RAIL_FLOAT_KEY: PanelId = 'toolRail';
// leftRail 全体のフロート既定幅 (px)。toolRail (アイコン列) は hug 幅だが、フロート
// 時は position:fixed で内容に応じて自然に縮む (`w-max` 相当) ため、widthPx には
// ドック既定 (TOOL_DOCK_WIDTH_DEFAULT) より狭い hug 向けの近似値を渡す。
const RAIL_FLOAT_WIDTH = 72;

export interface ModernLayoutProps {
  readonly state: RoomState;
  /** 現在のレイアウト (= 'modern')。LayoutToggle に渡す。 */
  readonly layout: LayoutKind;
  /** レイアウト切替 (= useLayoutPreference.setLayout、localStorage 永続)。 */
  readonly onLayoutChange: (next: LayoutKind) => void;
  /** 切替トグルを出すか (= スマホでは false、§9.4.1 ④)。 */
  readonly showToggle: boolean;
  /**
   * スマホ (= isPhoneViewport: 幅 < 640 ‖ coarse pointer × 短辺 < 640、
   * useLayoutPreference.isMobile = resize / matchMedia 追従) か。
   * true ならデスクトップシェルでなく **フォンシェル** (ModernPhoneShell = 全画面
   * canvas + 下部バー + ボトムシート) を描く (modern-phone-shell-2026-07-02.md §3.1
   * M1-b)。state / handler / panelRegistry は両シェル共有 (= このコンポーネントが保持)。
   */
  readonly isMobile: boolean;
  /**
   * §9.7 engine 永続化: 描画状態 store + engine 接続。RoomLayout が所有して渡す
   * (= 切替を跨いで永続)。canvas 本体は CanvasStage が描画し、ここでは chrome の
   * パネル (ツール/カラー/レイヤー/プレビュー) の駆動に使う。
   */
  readonly chrome: RoomChrome;
  /**
   * §9.7: canvas overflow scroll の anchor (= プレビューパネルが scroll 位置を読み書き)。
   * RoomLayout が所有し、CanvasStage の scroll コンテナと共有する。
   */
  readonly canvasScrollRef: React.RefObject<HTMLDivElement | null>;
  /**
   * §9.7: canvas 領域の「空スロット div」への ref。CanvasStage がこの矩形を計測して
   * absolute で重なる (= 見た目は今と同一、canvas 本体はここに無い)。
   */
  readonly canvasSlotRef: React.RefObject<HTMLDivElement | null>;
  /** 全消し確認モーダルの開閉 (RoomLayout が CanvasStage/chrome と共有)。 */
  readonly clearConfirmOpen: boolean;
  readonly setClearConfirmOpen: (open: boolean) => void;
  /**
   * 荒らし復旧 UI「?recover=<seq>」深リンク (vandalism-recovery-ui-2026-09-27.md
   * §2「入口を『続きを描く』に統一」)。RoomLayout が URL から読んで渡す。mount 時に
   * 値があれば過去キャンバス一覧モーダルを自動で開き (`PastCanvasListModal` の
   * `initialRecoverSeq` に橋渡し)、`onRecoverDeepLinkConsumed` で RoomLayout 側の
   * 状態をクリアしてもらう (= レイアウト切替 (remount) で再度自動オープンしない)。
   */
  readonly initialRecoverSeq?: number;
  readonly onRecoverDeepLinkConsumed?: () => void;
  /** 入室直後の「新機能の紹介」自動表示 (RoomLayout の useWhatsNewEntryAutoOpen が決める)。 */
  readonly whatsNewAutoOpen?: boolean;
  readonly onWhatsNewAutoOpened?: () => void;
}

// 第5スライス (レール端ドック): レール DnD 中の 4 スロット ドロップ位置インジケータ。
// 主 flex row (relative) を anchor に、canvas を挟む 4 隙間へ縦線を absolute 配置する。
//   leftOuter  = 左端          / leftInner  = canvas の左端 (左群の内側)
//   rightInner = canvas の右端  / rightOuter = 右端
//
// バグ修正 (2026-06-17): 旧版は内側 2 スロットを「row 中央 ±60px」のハードコード Tailwind で
// 近似していたため、内側ガイドが canvas 端でなく canvas 中央付近に出ていた。判定
// (railSlotNearPointer) と **同一の rect** (主 row / canvas 空スロット) を計測し、純関数
// railSlotIndicatorOffset で row 左端基準のオフセット px を求めて inline style `left` で渡す。
// これで「どのスロットに入るか (判定)」と「縦線の表示位置 (ガイド)」が一致する。
// geometry が無い (= 計測前の初回 dragover 1 tick) 間は CSS フォールバック (左端/canvas を
// 跨がない近似) で出し、計測後は px で正確に出す。
// activeSlot = 現ドロップ候補 (アクセント強調) / currentSlot = 現在地 (薄表示)。
const RAIL_SLOT_LABEL: Readonly<Record<RailSlot, string>> = {
  leftOuter: '左端',
  leftInner: '左 (キャンバス左隣)',
  rightInner: '右 (キャンバス右隣)',
  rightOuter: '右端',
};

// レール DnD インジケータの幾何 (= 主 row / canvas 空スロットを計測した clientRect)。
// railSlotNearPointer (判定) と同一ソースから取り、ガイド (railSlotIndicatorOffset) と一致させる。
interface RailGeometry {
  readonly rowLeft: number;
  readonly rowRight: number;
  readonly canvasLeft: number;
  readonly canvasRight: number;
  // S1 回帰修正 (2026-08-01): row の垂直範囲。railSlotNearPointer 自体は水平位置だけの
  // 判定関数 (= 縦方向は見ない) なので、「pointer が row の上にいるか」は呼び出し側が
  // この top/bottom で別途ゲートする (下記 updateRailDropSlot 参照)。
  readonly rowTop: number;
  readonly rowBottom: number;
}

const RailSlotIndicators = ({
  activeSlot,
  currentSlot,
  geometry,
}: {
  readonly activeSlot: RailSlot | null;
  readonly currentSlot: RailSlot;
  /** 計測済み row/canvas rect。null のときは計測前 (= 近似 CSS フォールバック)。 */
  readonly geometry: RailGeometry | null;
}): React.ReactNode => (
  <div className="pointer-events-none absolute inset-0 z-20" aria-hidden="true">
    {(['leftOuter', 'leftInner', 'rightInner', 'rightOuter'] as const).map((slot) => {
      const active = slot === activeSlot;
      const here = slot === currentSlot;
      // 計測済みなら canvas 実 rect から row 左端基準オフセット px を inline style で渡す
      // (= 判定と一致)。未計測なら端は left/right-0、内側は控えめな端寄せ近似で出す。
      const offset = geometry
        ? railSlotIndicatorOffset(
            slot,
            geometry.rowLeft,
            geometry.rowRight,
            geometry.canvasLeft,
            geometry.canvasRight,
          )
        : null;
      // 3px 幅の線を slot 境界の中央に乗せる (= -1.5px。leftOuter は内側、rightOuter は内側へ)。
      const style: React.CSSProperties = offset !== null ? { left: `${offset - 1.5}px` } : {};
      const fallbackClass =
        offset !== null
          ? ''
          : slot === 'leftOuter'
            ? 'left-0'
            : slot === 'rightOuter'
              ? 'right-0'
              : slot === 'leftInner'
                ? 'left-[33%]'
                : 'left-[66%]';
      return (
        <div
          key={slot}
          data-testid={`rail-slot-indicator-${slot}`}
          data-active={active ? 'true' : 'false'}
          data-slot-label={RAIL_SLOT_LABEL[slot]}
          style={style}
          className={`absolute inset-y-1 w-[3px] rounded-full transition-opacity ${fallbackClass} ${
            active
              ? 'bg-[var(--color-accent)] opacity-100'
              : here
                ? 'bg-[var(--color-accent)] opacity-30'
                : 'bg-[var(--color-text-muted)] opacity-15'
          }`}
        />
      );
    })}
  </div>
);

// S2 §11 (floating-panels-2026-08-01.md、consult 2026-08-01 追加確定「エッジスナップ」):
// 着地先を **面で示す半透明バンド** (row の全高 × その region のドック幅)。既存の 2px 挿入線
// (= `ModernPanelDragShell` の before/after インジケータ) は「列内のどこに入るか」を示す
// 別レイヤーとして併用する (= 「2px の挿入線だけではアフォーダンスが弱い」§11 動機2の解)。
const EdgeSnapBand = ({
  side,
  widthPx,
}: {
  readonly side: 'left' | 'right';
  readonly widthPx: number;
}): React.ReactNode => (
  <div
    aria-hidden="true"
    data-testid={`modern-edge-snap-band-${side}`}
    className={`pointer-events-none absolute inset-y-0 z-20 border-2 border-[var(--color-accent)] bg-[var(--color-accent)]/20 ${
      side === 'left' ? 'left-0' : 'right-0'
    }`}
    style={{ width: widthPx }}
  />
);

export const ModernLayout = ({
  state,
  layout,
  onLayoutChange,
  showToggle,
  isMobile,
  chrome,
  canvasScrollRef,
  canvasSlotRef,
  clearConfirmOpen,
  setClearConfirmOpen,
  initialRecoverSeq,
  onRecoverDeepLinkConsumed,
  whatsNewAutoOpen,
  onWhatsNewAutoOpened,
}: ModernLayoutProps): React.ReactNode => {
  const {
    transport,
    view,
    sendChat,
    setRom,
    sendKick,
    sendClear,
    // conflict-aware Undo (β Modern, undo-conflict-aware-2026-07-12.md): `stroke.cancel`
    // 送信. `useConflictAwareUndo` に thin に渡す (SessionProvider の command wrapper と
    // 同型, sendClear と並び).
    sendCancelStroke,
    sendCancelRegion,
    // custom-cursor-2026-09-19.md §2.3: カーソルエディタ (CursorEditorPopup) が使う。
    sendCursorChange,
    sendCursorDraft,
    leave,
    pushSystemLine,
    clearChatLog,
    filteredUids,
    toggleFilter,
    // 2026-06-08 ルーム設定ボタン (consult 2026-06-08): owner 判定に adminToken の
    // 有無を使う。RoomSettingsModal と同じ AdminTokenStore (SessionProvider 所有) を流用。
    adminTokens,
  } = useSession();

  // 全消し確認モーダル (Classic と同じく ConfirmDialog、新規プロトコルイベントなし)。
  // §9.7: open/setOpen は RoomLayout が所有 (chrome.handleClear が setOpen を叩く)。

  // 2026-06-08 ルーム設定モーダル (Classic と同じ RoomSettingsModal を配線、consult
  // 2026-06-08)。admin_token が無くてもモーダル側がガードして owner ログイン UI を出す
  // (= 非オーナーでも押せる、押すとログイン導線)。
  const [settingsOpen, setSettingsOpen] = useState(false);
  const inviteDialog = useInviteDialog(state.roomId);
  const whatsNew = useWhatsNew(undefined, undefined, {
    ...(whatsNewAutoOpen !== undefined ? { autoOpen: whatsNewAutoOpen } : {}),
    ...(onWhatsNewAutoOpened !== undefined ? { onAutoOpened: onWhatsNewAutoOpened } : {}),
  });
  // custom-cursor-2026-09-19.md §2.5: 「カーソル」エディタ (ephemeral、永続化しない).
  const [cursorEditorOpen, setCursorEditorOpen] = useState(false);
  // custom-cursor-ux フォローアップ (2026-09-19、作者 staging フィードバック §A-1):
  // 「一度も開いたことがない端末」判定 (= MemberPanel の自分の行「＋」プレースホルダを
  // pulse させるかどうか). Classic ClassicLayout と同じ localStorage キーを共有する
  // (= どちらのレイアウトで開いても以後両方で出なくなる、単一の「気づいた」フラグ).
  const [cursorHintSeen, setCursorHintSeen] = useState<boolean>(() =>
    typeof window === 'undefined' ? true : readCursorEditorHintSeen(window.localStorage),
  );
  // カーソル入口ボタン (ModernToolRail) の「New!」バッジ seen フラグ (2026-09-19 追補)。
  // 上記 cursorHintSeen (MemberPanel プレースホルダの pulse) とは別の演出・別
  // localStorage key だが、「開いたら消える」トリガは同じなので同じ funnel
  // (`openCursorEditor`) に相乗りさせる。Classic `CursorHeaderButton` と同じ store key
  // を共有 (= どちらのレイアウトのどの入口で開いても両方のバッジが消える).
  const cursorNewBadgeSeenStoreRef = useRef(createCustomCursorNewBadgeSeenStore());
  const [cursorNewBadgeSeen, setCursorNewBadgeSeen] = useState<boolean>(() =>
    cursorNewBadgeSeenStoreRef.current.hasSeen(),
  );
  // 回転ツールボタン (PC のみ) の「New!」バッジ seen (2026-10-03)。回転の全経路
  // (ボタン / R / Shift+Space / 2 本指ひねり / 左右回転) は useRoomChrome の
  // `onRotationChange` に合流するので、seen 化はここ 1 箇所で購読して行う。
  const rotateNewBadgeSeenStoreRef = useRef(createRotateNewBadgeSeenStore());
  const [rotateNewBadgeSeen, setRotateNewBadgeSeen] = useState<boolean>(() =>
    rotateNewBadgeSeenStoreRef.current.hasSeen(),
  );
  const { onRotationChange } = chrome;
  useEffect(() => {
    if (rotateNewBadgeSeen) return;
    return onRotationChange((r) => {
      if (r === 0) return;
      rotateNewBadgeSeenStoreRef.current.markSeen();
      setRotateNewBadgeSeen(true);
    });
  }, [rotateNewBadgeSeen, onRotationChange]);
  // 「開いた」を記録する単一の口 (どの入口 — ModernToolRail のボタン / MemberPanel の
  // 自分の行 — からでも通す). 常に「開く」(トグルではない).
  const openCursorEditor = useCallback((): void => {
    setCursorEditorOpen(true);
    setCursorHintSeen((seen) => {
      if (!seen && typeof window !== 'undefined') markCursorEditorHintSeen(window.localStorage);
      return true;
    });
    cursorNewBadgeSeenStoreRef.current.markSeen();
    setCursorNewBadgeSeen(true);
  }, []);
  // 自分の確定済みカーソル (= ModernToolRail ボタンのサムネ、カーソルエディタの編集初期値
  // の両方で使う単一の導出値、Classic ClassicLayout の `selfCursor` と対).
  const selfCursor = useMemo(
    () => state.members.find((m) => m.uid === state.uid)?.cursor ?? null,
    [state.members, state.uid],
  );
  // owner 判定 (= adminToken 有無)。AdminTokenStore.get は localStorage 直読みで
  // 非リアクティブなので、「ルーム設定」ボタンの見た目 (控えめ⇄琥珀アクセント) を
  // 駆動するため state 化する。初期値は mount 時の token 有無。
  const [isOwner, setIsOwner] = useState<boolean>(() => adminTokens.get(state.roomId) !== null);
  // モーダル close 時に owner 判定を再評価する単一経路 (= 直接 setSettingsOpen(false)
  // を渡さない)。AdminTokenStore の変化を subscribe する仕組みは無いので、モーダル内で
  // ログインして token を取得した直後にボタンを認証済表示へ更新するため close 経路で
  // 再評価する (Classic ClassicLayout.closeSettings 踏襲)。
  const closeSettings = useCallback((): void => {
    setSettingsOpen(false);
    setIsOwner(adminTokens.get(state.roomId) !== null);
  }, [adminTokens, state.roomId]);
  // 手のひら (pan) は CanvasStage が chrome.panActive を読んでドラッグ pan する。
  // Classic と同じ 2 入力ソース方式 (consult 2026-06-08): ツールバー「手のひら」持続
  // トグル `panToggleOn` (ローカル) と Space hold (useSpaceHoldPan が管理) の OR を
  // フックが chrome.setPanActive に流す。直近の「ボタン → setPanActive 直結」では
  // (1) Space hold pan が無い (2) Space の preventDefault が無くフォーカス中ボタンが
  // 既定の Space click で誤発火する、の 2 問題があったため Classic 構造に揃える。
  const [panToggleOn, setPanToggleOn] = useState<boolean>(false);
  useSpaceHoldPan({ panToggleOn, setPanActive: chrome.setPanActive });

  // §9.7: 描画 chrome の共通 state + engine 接続 (= chrome) と canvas scroll anchor
  // (= canvasScrollRef) は RoomLayout が所有して props で渡す。canvas 本体は CanvasStage
  // が描画し、ここでは chrome パネルの駆動と canvas 空スロット (canvasSlotRef) の配置だけ。

  // チャット文字サイズ。2026-06-12: 共有ヘッダ「表示」(UserPreferencesModal) から
  // 変更できるよう setter を持たせ、即時 localStorage に永続化する (Classic 踏襲)。
  const chatFontScaleStoreRef = useRef(createChatFontScalePreferenceStore());
  const [chatFontScale, setChatFontScaleState] = useState<ChatFontScale>(() =>
    chatFontScaleStoreRef.current.get(),
  );
  const setChatFontScale = useCallback((next: ChatFontScale): void => {
    setChatFontScaleState(next);
    chatFontScaleStoreRef.current.set(next);
  }, []);
  const chatFontEm = chatFontScaleToEm(chatFontScale);

  // 2026-06-12 共有ヘッダ「表示」(UserPreferencesModal) 配線。Modern にも Classic と
  // 同じ「表示」ボタン + UserPreferencesModal を出す (= ヘッダ完全一致化、ユーザ確定)。
  // ただし UserPreferencesModal の「サイドバーの大きさ」(sidebarScale) は **Classic
  // 固有** (= Classic 左カラム wrapper の zoom)。Modern にスケール対象サイドバーは無く
  // 視覚効果が出ない。値だけは Classic と同じ store (localStorage) に永続化して整合を
  // 保つが、Modern 固有の「表示」設定の取捨は後続スコープ (ユーザが反映先を指定予定)。
  const sidebarScaleStoreRef = useRef(createUiScalePreferenceStore());
  const [sidebarScale, setSidebarScaleState] = useState<UiScale>(() =>
    sidebarScaleStoreRef.current.getSidebar(),
  );
  const setSidebarScale = useCallback((next: UiScale): void => {
    setSidebarScaleState(next);
    sidebarScaleStoreRef.current.setSidebar(next);
  }, []);
  const [uiPrefsOpen, setUiPrefsOpen] = useState<boolean>(false);
  // 2026-06-12 ショートカット設定モーダル (shortcut-settings.md スライス2): ヘッダ
  // 「ショートカット」項目 + `?` ホットキーから開く (Classic と同一・layout 非依存)。
  const [shortcutsOpen, setShortcutsOpen] = useState<boolean>(false);
  // 2026-06-13 続報 (consult): ペン設定モーダル (手ぶれ補正 / AA)。ヘッダ「ペン」項目
  // から開く (Classic と同一の共有部品 = 特別扱いしない)。ModernToolProperties の
  // 既存トグルはそのまま (= 同じ共有 chrome 設定の別ビュー、二重の真実にしない)。
  const [penSettingsOpen, setPenSettingsOpen] = useState<boolean>(false);
  // A-4 背景色 ■ swatch (canvas-background-color §4.3): 背景色 ColorWheelPalette の
  // open-state。Modern の描画色ピッカー (ModernColorPanel) とは別。ModernLayerPanel の
  // ■ click が onToggleBackgroundColorWheel でこれを反転する。挙動は Classic と一致。
  const [bgColorWheelOpen, setBgColorWheelOpen] = useState<boolean>(false);
  // 2026-06-12 (shortcut-settings.md「Modern Tab 決着」): Tab で両サイドバー (左右
  // <aside>) を非表示にして canvas を最大化する。Classic は uiHidden = UI 全体隠し
  // だが、Modern は **両サイドバーのみ** (ヘッダ = 共有 RoomHeader は隠さない、ユーザ
  // 明示スコープ)。再度 Tab で復帰。state の流儀は Classic 踏襲 (useState<boolean>)。
  const [uiHidden, setUiHidden] = useState<boolean>(false);

  // ツールプロパティ「詳細」disclosure (手ぶれ補正 / AA / スポイトの取得元、
  // modern-brush-panel-2026-08-17.md §5.2)。既定 collapsed、localStorage に永続。
  // `ModernToolProperties` 自体は hook を持たない (= call-directly なユニットテスト
  // 様式のため) ので、開閉 state はここ (`chatFontScale`/`sidebarScale` と同じ
  // ローカル UI state パターン) に置き props で渡す。
  const toolDetailsStoreRef = useRef(createModernToolPropertiesDetailsPreferenceStore());
  const [toolDetailsOpen, setToolDetailsOpenState] = useState<boolean>(() =>
    toolDetailsStoreRef.current.get(),
  );
  const setToolDetailsOpen = useCallback((open: boolean): void => {
    setToolDetailsOpenState(open);
    toolDetailsStoreRef.current.set(open);
  }, []);

  // 太さプリセット (§9.11 太さプリセット、consult 2026-09-29)。明示登録型
  // (Procreate/MediBang 型、決定1): クリックで適用するだけでスライダでは登録値を
  // 変えない。「+」で現在値を登録、既定 3 個・最大 5 個 (`widthPresets.ts`)。
  // `ModernToolProperties` 自体は hook を持たないので、登録リストの state は
  // `toolDetailsOpen` と同じくここに置き props で渡す。
  const widthPresetStoreRef = useRef(createWidthPresetPreferenceStore());
  const [widthPresets, setWidthPresetsState] = useState<readonly number[]>(() =>
    widthPresetStoreRef.current.get(),
  );
  const handleAddWidthPreset = useCallback((): void => {
    setWidthPresetsState((prev) => {
      const next = addWidthPreset(prev, chrome.pen.width);
      widthPresetStoreRef.current.set(next);
      return next;
    });
  }, [chrome.pen.width]);
  const handleRemoveWidthPreset = useCallback((w: number): void => {
    setWidthPresetsState((prev) => {
      const next = removeWidthPreset(prev, w);
      widthPresetStoreRef.current.set(next);
      return next;
    });
  }, []);
  const handleResetWidthPresets = useCallback((): void => {
    widthPresetStoreRef.current.set(DEFAULT_WIDTH_PRESETS);
    setWidthPresetsState(DEFAULT_WIDTH_PRESETS);
  }, []);

  // §9.10 (consult 2026-06-14 第1 / 2026-06-16 第2スライス) ワークスペース preset。
  // 第1スライスの「左右ミラー boolean」を **名前付き Tier1 preset** へ一般化した
  // (§9.10 決定3): 標準 / 左右入替 (= 旧 UI左右入替) / お絵描き集中 / チャット重視。
  // 各 preset は「同一の 1 本のレイアウト state (order / panels / 表示・
  // 畳み / 幅) のスナップショットに名前を付けたもの」(§9.10 決定2)。
  // per-user localStorage 永続 (modernWorkspacePreference、旧 {mirror:true} も後方互換で
  // 読める → 'mirror' preset へ引き上げ)。
  //
  // 注意: これは「レイアウト配置」であって、ModernToolRail の「左右反転」ボタン
  // (= キャンバスの描画ミラー chrome.handleToggleFlip、canvas-flip.md) とは別機能。
  // 単独「UI左右入替」トグルは廃し、preset ピッカーの `左右入替` に吸収した (§9.10 決定4)。
  // §9.10 第3スライス (consult 2026-06-14 決定2「手動編集 = state の直接編集」): live state
  // を **preset 選択 (clean) か custom (手動編集済み)** の discriminated union (LiveWorkspace)
  // で持つ。preset を出発点に、ユーザーが パネル表示/畳み + ドック幅 を直接いじると custom
  // になる (= 名前付き preset から乖離)。名前付き preset を選び直すと preset へ戻り手動編集を
  // 破棄する (§9.10 決定2、確定挙動)。
  // 永続: clean なときは {presetId}、custom なときは full {workspace} を localStorage に保存
  // (prefFromLive/liveFromPref で相互変換。旧 {presetId}/{mirror} は後方互換で読める)。
  const workspaceStoreRef = useRef(createModernWorkspacePreferenceStore());
  // 左右 aside 要素への ref (= ドック幅 splitter が drag 開始時に境界 X を計測する anchor)。
  const leftAsideRef = useRef<HTMLElement>(null);
  const rightAsideRef = useRef<HTMLElement>(null);
  const [live, setLive] = useState<LiveWorkspace>(() =>
    liveFromPref(workspaceStoreRef.current.get()),
  );
  // live 更新の単一経路: state 更新 + 即 localStorage 永続 (custom は full state を保存)。
  const commitLive = useCallback((next: LiveWorkspace): void => {
    setLive(next);
    workspaceStoreRef.current.set(prefFromLive(next));
  }, []);
  // ピッカーで名前付き preset を選ぶ = preset モードへ戻り手動編集を破棄 (§9.10 決定2)。
  // 「カスタム」option は表示専用なので preset id 以外が来たら no-op (= 防御)。
  const setPreset = useCallback(
    (next: WorkspacePresetId): void => {
      commitLive(selectPreset(next));
    },
    [commitLive],
  );
  // `` ` `` キー (§9.10 決定4 layout.cycle-preset): Tier1 preset を宣言順に巡回する。
  // cycle は名前付き preset の選び直しなので custom 編集中でも preset へ戻る (= setPreset と同義)。
  // 巡回の起点: 現在 preset なら現 preset の次、custom なら標準 (= nextPresetId の未知扱い)
  // ではなく「標準の次」に飛ぶと直感に反するため、custom 時は標準から開始する。
  const cyclePreset = useCallback((): void => {
    setLive((prev) => {
      const base: WorkspacePresetId = prev.mode === 'preset' ? prev.presetId : 'standard';
      // custom からは「標準」へまず正規化したいので、preset なら次へ・custom なら標準そのもの。
      const next = prev.mode === 'preset' ? nextPresetId(base) : 'standard';
      const liveNext = selectPreset(next);
      workspaceStoreRef.current.set(prefFromLive(liveNext));
      return liveNext;
    });
  }, []);
  // live → 表示用 WorkspaceState (= 配置適用の単一経路)。order / 表示 / 幅 をこれから描く。
  const workspace = useMemo(() => resolveLive(live), [live]);

  // パネル表示名 (= パネル表示メニュー + aside aria ラベルで共有する単一語彙)。
  // canvas は常に表示なので toggle メニューには出さない (= §9.7 死守。リストにも載せない)。
  const PANEL_ARIA: Readonly<Record<PanelId, string>> = {
    toolRail: 'ツール',
    commandCluster: '操作',
    brushPanel: 'ブラシ',
    toolProperties: 'ペン設定',
    colorPanel: 'カラー',
    colorPalette: 'パレット',
    canvas: 'キャンバス',
    previewPanel: 'プレビュー',
    layerPanel: 'レイヤー',
    socialDock: 'チャット',
  };
  // パネル表示メニューの項目 (= canvas を除く全パネル、現在の表示状態つき)。順序は
  // workspace の order → region 内 panels 並び (= 画面の見た目順) に合わせて読む。
  // S2 (floating-panels-2026-08-01.md): フロート中パネルは region の panels 配列から
  // 除かれる (= §6 決定4「docked と floating は排他」) ので、docked 分の後ろに個別で足す
  // (= 「隠したパネルもここで戻せる」discoverable な復帰経路をフロート中パネルにも保つ)。
  const dockedToggleItems = workspace.order.flatMap((region) =>
    workspace.panels[region]
      .filter((id) => id !== 'canvas')
      .map((id) => ({ id, label: PANEL_ARIA[id], visible: isPanelVisible(workspace, id) })),
  );
  const floatingToggleItems = (Object.keys(workspace.floating ?? {}) as PanelId[]).map((id) => ({
    id,
    label: PANEL_ARIA[id],
    visible: isPanelVisible(workspace, id),
  }));
  const panelToggleItems = [...dockedToggleItems, ...floatingToggleItems];
  // 手動編集ハンドラ (§9.10 第3スライス): いずれも現 live を出発点に custom 化する。
  // パネル表示トグル (= ModernPanelVisibilityMenu / hidden 直接編集)。canvas は不可。
  const handleTogglePanel = useCallback(
    (id: PanelId, visible: boolean): void => {
      // hidden=!visible。canvas は togglePanelHidden 側で hide 無視 (= §9.7 死守)。
      commitLive(togglePanelHidden(live, id, !visible));
    },
    [commitLive, live],
  );
  // ドック幅リサイズ (= ModernDockSplitter)。region 群 (左/右 aside) 単位で width を更新。
  const handleResizeAside = useCallback(
    (regions: ReadonlyArray<WorkspaceRegion>, widthPx: number): void => {
      commitLive(setLiveAsideWidth(live, regions, widthPx));
    },
    [commitLive, live],
  );

  // §9.10 第4スライス (決定1 C「ドック内並べ替え + パネル間移動(ドラッグ)」) + S1
  // (floating-panels-2026-08-01.md §3 決定2、2026-08-01): パネルをドラッグして並べ替え/
  // 別 region へ移動する。ドラッグ中の state (掴んでいるパネル id + 現ドロップ候補) は
  // ここで持ち、確定したら movePanelLive で live を custom 化する (= 既存の表示/幅編集と
  // 同じ custom 経路・{workspace} 永続)。
  //   - draggingId: 今掴んでいるパネル (null ならドラッグ無し)。
  //   - dropTarget: { region, index } = ドロップ確定時の挿入先 (= movePanel の toRegion/toIndex)。
  // canvas は ModernPanelDragShell でラップしない (= grip を出さない) ので掴めず、
  // movePanel も canvas / canvas region を no-op で弾く (= §9.7 計測スロット二重死守)。
  //
  // S1: HTML5 DnD (draggable/dragover/drop) から pointer events へ全面移行した
  // (ModernPanelDragShell.tsx 冒頭コメント参照)。ヒットテスト (= 今どのパネル/region の
  // 上にいるか) はネイティブ dragover が教えてくれなくなったので、ドラッグ中の
  // pointermove ごとに `collectPanelDropCandidates` で DOM (data-panel-id / data-region
  // 属性) を計測し、純関数 `resolvePanelDropTarget` (layouts/shared/panelDragGeometry.ts)
  // で判定する。判定結果を旧来の `handlePanelDragOver` / `handleRegionDragOverEnd`
  // (= 意思決定・leftRail ガードは無改変) へ振り分ける。
  const [draggingId, setDraggingId] = useState<PanelId | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  // S2 (floating-panels-2026-08-01.md §2 決定1): ドラッグ中、ポインタが「ドック外 (吸着
  // マージンの外)」にあるときの座標 (= 真のフロート候補)。dropTarget と排他 (どちらか一方
  // だけが non-null、handlePointerDragMove が都度どちらかを立てる)。pointerup で
  // dropTarget===null かつ floatPreview!==null ならフロートへ確定する。
  const [floatPreview, setFloatPreview] = useState<{ x: number; y: number } | null>(null);
  // S2 §11 改訂 (floating-panels-2026-08-01.md、consult 2026-08-01「掴んだ位置を保持する」):
  // 実測 grab-offset (= 掴んだ点と対象要素左上の相対距離)。`handlePanelDragStart` で
  // pointerdown 時点の実測値をセットし、ドラッグ中ずっと同じ値を保持する (= 固定値
  // `FLOAT_GRAB_OFFSET` での近似を廃止)。`floatPositionFromPointer` を呼ぶ 3 箇所
  // (`handlePanelDragEnd` の着地点計算 / フロート層のドラッグ中描画 / detach ゴースト) が
  // すべてこの単一の state を参照する (= 判定系を 2 つ作らない)。ドラッグ終了で null に戻す。
  const [grabOffset, setGrabOffset] = useState<FloatPoint | null>(null);
  // S2 §11 (floating-panels-2026-08-01.md、consult 2026-08-01 追加確定「エッジスナップ」):
  // ドラッグ中、ポインタが layout row の左右端 (`EDGE_SNAP_THRESHOLD_PX` 以内) に入っている
  // 側。dropTarget と併存しうる (= エッジスナップは dropTarget を立てる「ドック確定」の一種、
  // §11 優先順位③) が、`floatPreview` とは排他 (エッジスナップが成立すればフロートにしない)。
  // 描画 (半透明バンド) 専用の表示 state で、確定判定そのものは dropTarget が担う。
  const [edgeSnapSide, setEdgeSnapSide] = useState<'left' | 'right' | null>(null);
  // S2 (§9 z-order「クリック/ドラッグ開始で最前面へ」): フロートパネルの重なり順
  // (背面→前面の PanelId 列)。永続化しない (= reload で declaration 順に戻ってよい、
  // §6 が永続対象と定めるのは配置・座標のみ)。
  const [floatOrder, setFloatOrder] = useState<ReadonlyArray<PanelId>>([]);
  // S2 バグ修正 (2026-08-01 実機フィードバック「背の低いフロートが画面下まで移動できない」):
  // panelId → 実測サイズ (FloatShell.onMeasure が mount + ResizeObserver で報告)。ドラッグ中の
  // 描画位置 clamp と drop 確定の着地点 clamp の**両方**がこれを参照する (= floatSizeFor)。
  // 永続化しない (= セッションローカルな実測キャッシュ、リロードすれば FloatShell が
  // 再マウント時に再報告する)。
  const [floatSizes, setFloatSizes] = useState<Partial<Record<PanelId, FloatSize>>>({});
  // ドラッグ位置 / 着地点 clamp に使うサイズの単一導出経路: 実測済みならそれを使い、
  // 未実測 (= 今セッションでこの panelId をまだ一度も浮かせていない) なら
  // floatWidthFor + FLOAT_PANEL_FALLBACK_HEIGHT で近似する (= 初回のみの近似、
  // floatShellGeometry.ts の FLOAT_PANEL_FALLBACK_HEIGHT コメント参照)。
  const floatSizeFor = useCallback(
    (id: PanelId): FloatSize =>
      floatSizes[id] ?? {
        // §12: leftRail (RAIL_FLOAT_KEY) は hug 幅の細いレールなのでドック既定幅
        // (TOOL_DOCK_WIDTH_DEFAULT=244) を fallback にすると初回 clamp が過大になる。
        width: id === RAIL_FLOAT_KEY ? RAIL_FLOAT_WIDTH : floatWidthFor(id),
        height: FLOAT_PANEL_FALLBACK_HEIGHT,
      },
    [floatSizes],
  );
  // FloatShell.onMeasure の受け手: サブピクセルの揺れで無駄な再 render を起こさないよう
  // sizeEquals で有意差の無い更新を捨てる (floatingSlot.slotRectEquals と同じ流儀)。
  const handleFloatMeasure = useCallback((id: PanelId, size: FloatSize): void => {
    setFloatSizes((prev) => {
      const cur = prev[id];
      if (cur !== undefined && sizeEquals(cur, size)) return prev;
      return { ...prev, [id]: size };
    });
  }, []);
  // 第5スライス (レール端ドック): レール (leftRail region) を掴んでいるか + 現ドロップスロット。
  // レール grip ドラッグは panel-DnD でなく **region 移動** に分岐する (= 4 スロットへ)。
  // draggingId は (見た目: 掴んだパネルの半透明化のため) 通常通りセットするが、
  // railDragging=true の間は panel の dropTarget でなく railDropSlot を使う。
  const [railDragging, setRailDragging] = useState<boolean>(false);
  const [railDropSlot, setRailDropSlot] = useState<RailSlot | null>(null);
  // ドロップインジケータ縦線の表示位置を出すための計測幾何 (= 主 row / canvas 空スロットの
  // clientRect)。railSlotNearPointer (判定) と **同一ソース** から取り、ガイドと判定をズラさ
  // ない (バグ修正 2026-06-17: 旧版は中央 ±60px ハードコードで内側ガイドが canvas 中央に出た)。
  const [railGeometry, setRailGeometry] = useState<RailGeometry | null>(null);
  // canvas 空スロット (canvasSlotRef) と外枠 row の rect から 4 スロットを幾何判定するため、
  // 主 flex row への ref を持つ (= railSlotNearPointer / インジケータ位置に rect を渡す)。
  const layoutRowRef = useRef<HTMLDivElement>(null);
  // row / canvas 空スロットの最新 rect を計測して幾何 state を更新する単一経路。dragover ごと +
  // ドラッグ開始時 + drag 中の layout 変化 (ResizeObserver) で呼び、ガイドを常に最新値で描く。
  const measureRailGeometry = useCallback((): RailGeometry | null => {
    const row = layoutRowRef.current;
    const canvasEl = canvasSlotRef.current;
    if (!row || !canvasEl) return null;
    const rowRect = row.getBoundingClientRect();
    const canvasRect = canvasEl.getBoundingClientRect();
    const geo: RailGeometry = {
      rowLeft: rowRect.left,
      rowRight: rowRect.right,
      canvasLeft: canvasRect.left,
      canvasRight: canvasRect.right,
      rowTop: rowRect.top,
      rowBottom: rowRect.bottom,
    };
    setRailGeometry(geo);
    return geo;
  }, [canvasSlotRef]);
  const handlePanelDragStart = useCallback(
    (id: PanelId, offset: FloatPoint): void => {
      setDraggingId(id);
      setDropTarget(null);
      // S2: 前回ドラッグの残骸防御 (通常 handlePanelDragEnd で既にクリア済のはずだが、
      // 新しいドラッグの開始時点で必ず「まだ判定していない」状態にする)。
      setFloatPreview(null);
      setEdgeSnapSide(null);
      // S2 §11 改訂 (consult 2026-08-01「掴んだ位置を保持する」): pointerdown 時点で実測
      // された grab-offset (`ModernPanelDragShell`/`FloatShell` の `measureGrabOffset`、
      // 基準はそれぞれ `[data-panel-id]` / FloatShell root) を保持する。ドック内パネルと
      // フロート時で幅が変わりうる (`floatWidthFor` はドック既定幅、ドックは splitter で
      // 可変幅) ため、フロート実寸 (`floatSizeFor`) の範囲へクランプしてから保持し、掴み点が
      // フロート本体/detach ゴーストの外に出ないようにする (§11「detach 時の注意」)。
      setGrabOffset(clampGrabOffset(offset, floatSizeFor(id)));
      // S2 バグ修正 (2026-08-01「初回 detach が (定数フォールバック前提の) y=518 に
      // 跳ね上がる」): floatSizes にまだ実測値が無い panelId (= 今セッションで一度も
      // フロートしたことが無い) は、ドラッグ開始の時点でまだドック内に描画されている
      // パネルの DOM 実高さを **初期値として** seed する。着地点計算 (handlePanelDragEnd)
      // とドラッグ中の追従描画 (floating layer の render map) が floatSizeFor 経由でこれを
      // 拾うので、初回 detach から FLOAT_PANEL_FALLBACK_HEIGHT (400px 固定) でなく実寸に
      // 近い値で clamp できる。
      //   - 幅はここでは測らない: FloatShell は widthPx (= floatWidthFor(id)、ドック既定幅)
      //     を常に強制描画するため、seed する高さと対で使う幅も floatWidthFor(id) に揃える
      //     (= ドックの実測幅 (splitter で可変) をそのまま使うと、実際にフロートで描かれる
      //     幅と食い違って clamp の x 側だけズレる)。
      //   - 既に実測値がある (= 過去に一度でもフロートして onMeasure 済み、あるいは今回既に
      //     seed 済み) なら上書きしない (= 「初期値」に徹する、より正確な実測を保つ)。
      //   - フロート中パネルの再配置ドラッグ (FloatShell の grip → 同じ handlePanelDragStart)
      //     では data-panel-id を持つドック DOM が存在しない (= querySelector が null) ので
      //     この分岐は何もしない (= 既存の onMeasure 実測値をそのまま使う)。
      //   - mount 後の FloatShell.onMeasure が次フレームで正確な値に上書き補正する
      //     (= あくまで初期値、ドックとフロートで高さが多少ズレても自己修復される)。
      if (!isRailPanel(id)) {
        setFloatSizes((prev) => {
          if (prev[id] !== undefined) return prev;
          const el = layoutRowRef.current?.querySelector<HTMLElement>(`[data-panel-id="${id}"]`);
          if (!el) return prev;
          const rect = el.getBoundingClientRect();
          if (rect.width === 0 || rect.height === 0) return prev;
          return { ...prev, [id]: { width: floatWidthFor(id), height: rect.height } };
        });
      }
      // レールパネル (toolRail / commandCluster) の grip → region 移動モード。
      if (isRailPanel(id)) {
        setRailDragging(true);
        setRailDropSlot(null);
        // 開始時点の row/canvas rect を即計測 (= 最初の dragover を待たずガイドを正しく出す)。
        measureRailGeometry();
      } else {
        setRailDragging(false);
      }
    },
    [measureRailGeometry, floatSizeFor],
  );
  // パネル上 dragover 相当: そのパネルの before(index) / after(index+1) を挿入位置候補にする。
  // レール移動中は panel の dropTarget を更新しない (= region 移動セマンティクスなので無視)。
  // leftRail はレール専用の閉じたユニット (= toolRail/commandCluster のみ。region ごと 4 スロット
  // 移動する端ドック) なので、wide パネル (= 非レールパネル) のドロップ先にしない: leftRail を
  // 候補にしない (= dropTarget を立てない) ことでスリム hug 幅の leftRail に wide パネルが挿入
  // されてレイアウトが破綻するのを防ぐ。ここに来るのは非レールパネルのドラッグ中 (railDragging
  // false) なので region === 'leftRail' のときは弾けば十分 (= レール内はドロップ不可)。
  const handlePanelDragOver = useCallback(
    (region: WorkspaceRegion, index: number, insertAfter: boolean): void => {
      if (railDragging) return;
      if (region === 'leftRail') return;
      setDropTarget({ region, index: insertAfter ? index + 1 : index });
    },
    [railDragging],
  );
  // 空 region (= 全パネル別所へ移動した region) や末尾余白へのドロップ候補。レール中は無視。
  // leftRail はレール専用ユニットなので wide パネルのドロップ先にしない (= handlePanelDragOver
  // と同じガード。leftRail シェルの末尾余白へ重ねても候補にならない)。
  const handleRegionDragOverEnd = useCallback(
    (region: WorkspaceRegion, endIndex: number): void => {
      if (railDragging) return;
      if (region === 'leftRail') return;
      setDropTarget({ region, index: endIndex });
    },
    [railDragging],
  );
  // S1: パネル DnD のヒットテスト用に、row 配下の全パネルシェル (data-panel-id) と全 region
  // コンテナ (data-region) の矩形を DOM 計測する。純関数 resolvePanelDropTarget にそのまま渡す
  // 形 (PanelDropCandidate[] / RegionEndCandidate[]) に整形する (= DOM 計測はここに閉じ、判定
  // ロジック自体は panelDragGeometry.ts の純関数、oekaki/CLAUDE.md jsdom race 回避の定石と同じ
  // 「DOM 計測 / 純ロジック分離」)。幅・高さ 0 (= uiHidden 等で display:none) の矩形は候補から
  // 除く (= 非表示要素が (0,0) 付近のポインタに誤ヒットしないための最小防御)。
  const collectPanelDropCandidates = useCallback((): {
    readonly panels: ReadonlyArray<PanelDropCandidate<WorkspaceRegion, PanelId>>;
    readonly regionEnds: ReadonlyArray<RegionEndCandidate<WorkspaceRegion>>;
  } => {
    const row = layoutRowRef.current;
    if (!row) return { panels: [], regionEnds: [] };
    const panels: Array<PanelDropCandidate<WorkspaceRegion, PanelId>> = [];
    for (const el of row.querySelectorAll<HTMLElement>('[data-panel-id]')) {
      const panelId = el.dataset.panelId as PanelId | undefined;
      const region = el.dataset.region as WorkspaceRegion | undefined;
      const index = el.dataset.index !== undefined ? Number(el.dataset.index) : Number.NaN;
      if (!panelId || !region || Number.isNaN(index)) continue;
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      panels.push({
        panelId,
        region,
        index,
        rect: { left: r.left, top: r.top, width: r.width, height: r.height },
      });
    }
    const regionEnds: Array<RegionEndCandidate<WorkspaceRegion>> = [];
    for (const el of row.querySelectorAll<HTMLElement>('[data-region]')) {
      const region = el.dataset.region as WorkspaceRegion | undefined;
      const endIndex = el.dataset.endIndex !== undefined ? Number(el.dataset.endIndex) : Number.NaN;
      if (!region || Number.isNaN(endIndex)) continue;
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      regionEnds.push({
        region,
        endIndex,
        rect: { left: r.left, top: r.top, width: r.width, height: r.height },
      });
    }
    return { panels, regionEnds };
  }, []);

  // 第5スライス: レール DnD 中の pointer 位置 → 4 スロットの最寄りを判定。canvas 空スロット
  // (canvasSlotRef) の rect を基準に算出する。判定とガイドを同一 rect で動かす (= ガイドの
  // railGeometry とポインタ判定が必ず一致)。
  //
  // 回帰修正 (2026-08-01 実機検証、パネル側と同種の懸念): 旧 HTML5 DnD はこの row 要素に
  // 実際にポインタが乗っているときだけ `dragover`/`drop` が発火した (= row の外側へ出れば
  // 自然にヒットしない) が、pointer capture 版はこのゲートが無いので row の縦範囲を明示
  // チェックし、外なら `railDropSlot` を null に戻す。
  //
  // バグ修正 (2026-08-01 実機 Playwright 検証、§12 追加確定): 上記の row 矩形ガードだけでは
  // 不十分だった。**row の実測矩形はヘッダ帯を除く画面ほぼ全域** (= canvas 中央も row の内側)
  // なので、旧 `railSlotFromPointer` (= 常にいずれかのスロットを返す全域関数) を使う限り
  // 「キャンバス中央でリリースしてもフロート化できない」実害が残っていた (S1 で row 矩形
  // ガードにより解消したのと同じバグクラスの残り — 全域関数には「近接」の概念が無い)。
  // `railSlotNearPointer` (= 4 スロットそれぞれに `RAIL_SLOT_SNAP_MARGIN_PX` の吸着圏を持たせ、
  // どの圏内にも入っていなければ `null` を返す) に差し替えた。これにより「キャンバス中央へ
  // 引っ張る = フロート」「左右端 (スロット位置) へ運ぶ = 収まる」という、通常パネルの
  // 「① 直接ヒット → ② 40px 吸着 (`resolveDockSnapTarget`) → ③ エッジスナップ → ④ フロート」
  // と同じ心的モデルになる (= レールは「① 4 スロット吸着 → ② フロート」の 2 段、直接ヒット/
  // エッジスナップに相当する中間層は元々不要 — 4 スロット自体が「近接判定つきの吸着」に
  // なったため)。戻り値 (= スロット or null) を呼び出し側 (handlePointerDragMove) が読んで
  // floatPreview の立て下ろしを行う。
  const updateRailDropSlot = useCallback(
    (clientX: number, clientY: number): RailSlot | null => {
      const geo = measureRailGeometry();
      if (!geo) {
        setRailDropSlot(null);
        return null;
      }
      if (
        clientY < geo.rowTop ||
        clientY > geo.rowBottom ||
        clientX < geo.rowLeft ||
        clientX > geo.rowRight
      ) {
        setRailDropSlot(null);
        return null;
      }
      const slot = railSlotNearPointer(
        clientX,
        geo.rowLeft,
        geo.rowRight,
        geo.canvasLeft,
        geo.canvasRight,
      );
      setRailDropSlot(slot);
      return slot;
    },
    [measureRailGeometry],
  );

  // S1: ドラッグ中の pointermove を一元的に受け取る (ModernPanelDragShell の grip から座標
  // だけが渡ってくる)。railDragging かどうかでレール4スロット判定 / パネル DnD ヒットテスト
  // へ振り分ける (= 判定ロジックの詳細はここに閉じ、シェル側は座標を伝えるだけ)。
  const handlePointerDragMove = useCallback(
    (clientX: number, clientY: number): void => {
      if (railDragging) {
        // §12: row の縦範囲内 (= 4 スロットのどこかにヒット) なら従来どおりドック確定候補。
        // 範囲外なら「フロート候補」= 通常パネルの floatPreview と同じ状態にする
        // (= 既存の detach プレビューゴースト・エッジスナップ不要の判定を素直に共有)。
        const slot = updateRailDropSlot(clientX, clientY);
        setFloatPreview(slot === null ? { x: clientX, y: clientY } : null);
        return;
      }
      const { panels, regionEnds } = collectPanelDropCandidates();
      // leftRail 自身のパネル (toolRail/commandCluster) は候補から除く (= handlePanelDragOver
      // が region==='leftRail' を no-op で弾くのと同じ既存ガードを、判定の入口でも効かせる)。
      // バグ修正 (2026-08-01 実機検証、エッジスナップ実装で発覚): leftRail は画面左端の細い
      // アイコン列で、EDGE_SNAP_THRESHOLD_PX (64px) の帯とほぼ重なる。除外しないまま
      // resolvePanelDropTarget に渡すと、左端へドラッグしたポインタが toolRail/commandCluster
      // の矩形に直接ヒットして `resolution.kind==='panel'` (①最優先) が成立してしまい、
      // handlePanelDragOver 側は no-op で dropTarget を更新しないのに
      // setFloatPreview(null)/setEdgeSnapSide(null) だけは実行されてしまう。結果、ちょうど
      // leftRail アイコンの上を通過した瞬間だけエッジスナップの帯が消え、かつ dropTarget も
      // 立たない (= ②③④のどれにも判定が進まない) 状態になり、そこで pointerup すると
      // 何も起きない (フロートにもドックにもならない) 死角ができていた。leftRail 自身の
      // パネルは非レールパネルの着地先に絶対なりえないので、判定の入口で除いてよい。
      // 同じ理由で region-end 候補 (= leftRail 自身の列コンテナ) も除く。この 1 つの配列を
      // 以降すべての判定 (①panel/region-end、②40px 吸着) で使い回す (= 除外を 1 箇所に集約)。
      const dockablePanels = panels.filter((p) => p.region !== 'leftRail');
      const dockableRegionEnds = regionEnds.filter((r) => r.region !== 'leftRail');
      const resolution = resolvePanelDropTarget(
        { x: clientX, y: clientY },
        dockablePanels,
        dockableRegionEnds,
        draggingId,
      );
      if (resolution?.kind === 'panel') {
        handlePanelDragOver(resolution.region, resolution.index, resolution.insertAfter);
        setFloatPreview(null);
        setEdgeSnapSide(null);
      } else if (resolution?.kind === 'region-end') {
        handleRegionDragOverEnd(resolution.region, resolution.endIndex);
        setFloatPreview(null);
        setEdgeSnapSide(null);
      } else {
        // resolution === null (= どのパネル/region 矩形にもポインタが無い、ドック外)。
        // 回帰修正 (2026-08-01 実機検証): ここで何もしないと直前の dropTarget が残り、
        // ドック外でリリースしても「最後に通過した候補」へ確定してしまう (= 旧 HTML5 DnD
        // ではドロップゾーン外リリースは dragover が来ず drop も発火しない = キャンセル
        // だったのに対する回帰)。null を明示的に反映してキャンセル可能な状態に戻す。
        //
        // S2 (floating-panels-2026-08-01.md §2 決定1「ドック優先の吸着」): 直接ヒットしなく
        // ても region 矩形からマージン内 (`DOCK_SNAP_MARGIN_PX`) ならドックが勝つ (= 「端に
        // 置きたいのに浮いてしまう」誤爆を防ぐ)。leftRail は wide パネルの吸着先にしない
        // (= handlePanelDragOver/handleRegionDragOverEnd と同じ既存ガードと揃える)。
        const snap = resolveDockSnapTarget(
          { x: clientX, y: clientY },
          dockableRegionEnds,
          DOCK_SNAP_MARGIN_PX,
        );
        if (snap !== null) {
          setDropTarget({ region: snap.region, index: snap.endIndex });
          setFloatPreview(null);
          setEdgeSnapSide(null);
        } else {
          // S2 §11 (floating-panels-2026-08-01.md、consult 2026-08-01 追加確定
          // 「エッジスナップ」): ①②とも外れたら、collapse した (= regionEnds に候補が
          // 無い) region にも戻せるよう layout row の物理端 (画面端基準、列矩形に依存
          // しない) を判定する。動機: rightDock を全部フロートにして collapse させると、
          // その region は regionEnds 候補として存在しなくなり①②のどちらにも引っかから
          // ず「ドラッグで出したフロートをドラッグで戻せない」行き止まりになっていた。
          const row = layoutRowRef.current;
          const rowRect = row?.getBoundingClientRect() ?? null;
          const edgeSide = rowRect
            ? resolveEdgeSnapSide(
                { x: clientX, y: clientY },
                {
                  left: rowRect.left,
                  top: rowRect.top,
                  width: rowRect.width,
                  height: rowRect.height,
                },
                EDGE_SNAP_THRESHOLD_PX,
              )
            : null;
          const region = edgeSide !== null ? edgeSnapRegion(workspace, edgeSide) : null;
          if (edgeSide !== null && region !== null) {
            setDropTarget({ region, index: workspace.panels[region].length });
            setFloatPreview(null);
            setEdgeSnapSide(edgeSide);
          } else {
            // 真のフロート候補 (= マージン内にもエッジにもドックが無い)。座標は生の
            // pointer 位置を保持し、着地点への変換 (実測 grab-offset 引き + viewport clamp)
            // は render 時 / commit 時に floatPositionFromPointer + clampFloatPosition で
            // 行う (= 座標変換の単一ソース、handlePanelDragEnd と floating レイヤーの render/
            // detach ゴーストで共有)。
            setDropTarget(null);
            setFloatPreview({ x: clientX, y: clientY });
            setEdgeSnapSide(null);
          }
        }
      }
    },
    [
      railDragging,
      updateRailDropSlot,
      collectPanelDropCandidates,
      draggingId,
      handlePanelDragOver,
      handleRegionDragOverEnd,
      workspace,
    ],
  );

  // タッチのゴーストクリック対策 (oekaki/CLAUDE.md「タッチのゴーストクリックはオーバーレイを
  // 即閉じる」と同種の懸念、floating-panels-2026-08-01.md §9 落とし穴): ドロップ確定直後、
  // 着地点に着弾する互換 click (touch 由来) がパネル内のボタンを誤爆しないよう、確定直後の
  // 短時間だけ次の 1 click を飲み込む。HTML5 DnD には無かった懸念 (= drag ghost が実要素を
  // 覆っていたので着地点の click は起きなかった) だが、挙動不変どころか従来より堅くなる方向
  // なので追加してよい (S1 スコープの明示許可、floating-panels-2026-08-01.md §9)。
  const ghostClickGuardArmedRef = useRef(false);
  const ghostClickGuardTimeoutRef = useRef<number | null>(null);
  const armGhostClickGuard = useCallback((): void => {
    ghostClickGuardArmedRef.current = true;
    if (ghostClickGuardTimeoutRef.current !== null) {
      window.clearTimeout(ghostClickGuardTimeoutRef.current);
    }
    ghostClickGuardTimeoutRef.current = window.setTimeout(() => {
      ghostClickGuardArmedRef.current = false;
      ghostClickGuardTimeoutRef.current = null;
    }, GHOST_CLICK_GUARD_MS);
  }, []);
  useEffect(
    () => () => {
      if (ghostClickGuardTimeoutRef.current !== null) {
        window.clearTimeout(ghostClickGuardTimeoutRef.current);
      }
    },
    [],
  );

  // ドラッグ終了 (S1: pointerup=committed / pointercancel・Escape=キャンセル の単一経路)。
  // 旧 handlePanelDragEnd (cleanup) + handlePanelDrop (パネル確定) + handleRailDrop
  // (レール確定) を統合した (= native DnD の dragend/drop 2 イベントが pointer 版では
  // pointerup 1 イベントに畳まれるため)。
  const handlePanelDragEnd = useCallback(
    (committed: boolean): void => {
      if (committed) {
        if (railDragging) {
          if (railDropSlot !== null) {
            commitLive(moveRailLive(live, railDropSlot));
          } else if (floatPreview !== null) {
            // §12 (floating-panels-2026-08-01.md、方針変更 2026-08-01): 4 スロットの
            // どこにもヒットしないままリリース = leftRail 全体を 1 ユニットとして
            // フロートへ切り離す (通常パネルの detach と同じ着地点計算、
            // RAIL_FLOAT_KEY を代表 id にして z-order/size lookup を共有する)。
            const landing = clampFloatPosition(
              floatPositionFromPointer(
                floatPreview.x,
                floatPreview.y,
                grabOffset ?? FLOAT_GRAB_OFFSET,
              ),
              floatSizeFor(RAIL_FLOAT_KEY),
              viewportSizeNow(),
              FLOAT_EDGE_INSET_PX,
            );
            commitLive(floatRailLive(live, landing));
            setFloatOrder((order) => bringToFront(order, RAIL_FLOAT_KEY));
          }
        } else if (draggingId !== null && dropTarget !== null) {
          // ドックへ確定 (= 通常の並べ替え/移動、または既存フロートパネルの re-dock。
          // movePanel が floating エントリの後始末も担うので経路は 1 本のまま)。
          commitLive(movePanelLive(live, draggingId, dropTarget.region, dropTarget.index));
        } else if (draggingId !== null && floatPreview !== null) {
          // S2: ドック外で確定 = フロートへ切り離す / 既存フロートパネルを再配置する。
          // 着地点は最後に観測した pointer 座標から算出する (= handlePointerDragMove と同じ
          // floatPositionFromPointer、座標変換の単一ソース)。offset は handlePanelDragStart
          // で実測・保持した grabOffset (§11「掴んだ位置を保持する」、null は理論上到達しない
          // 防御的フォールバックのみ FLOAT_GRAB_OFFSET)。clamp サイズは floatSizeFor
          // (= 実測済みならそれを使う。バグ修正 2026-08-01: 以前は常に
          // FLOAT_PANEL_FALLBACK_HEIGHT 固定で clamp していたため、背の低いパネルが実際の
          // 可動域より手前で頭打ちになっていた)。今セッションで一度も浮かせていない
          // panelId だけ近似値 (floatWidthFor + FLOAT_PANEL_FALLBACK_HEIGHT) を使う。
          const landing = clampFloatPosition(
            floatPositionFromPointer(
              floatPreview.x,
              floatPreview.y,
              grabOffset ?? FLOAT_GRAB_OFFSET,
            ),
            floatSizeFor(draggingId),
            viewportSizeNow(),
            FLOAT_EDGE_INSET_PX,
          );
          commitLive(floatPanelLive(live, draggingId, landing));
          setFloatOrder((order) => bringToFront(order, draggingId));
        }
        armGhostClickGuard();
      }
      setDraggingId(null);
      setDropTarget(null);
      setFloatPreview(null);
      setEdgeSnapSide(null);
      setGrabOffset(null);
      setRailDragging(false);
      setRailDropSlot(null);
      setRailGeometry(null);
    },
    [
      commitLive,
      draggingId,
      dropTarget,
      floatPreview,
      floatSizeFor,
      grabOffset,
      live,
      railDragging,
      railDropSlot,
      armGhostClickGuard,
    ],
  );

  // ドラッグ中に layout (パネル表示/幅/ウィンドウ resize) が変わると canvas rect が動くので、
  // row / canvas 空スロットを ResizeObserver + window resize で監視し最新値で再計測する
  // (= ガイドが古い rect のまま残らない。判定は pointermove で都度測るので、ここはガイドの追従)。
  useEffect(() => {
    if (!railDragging) return;
    const row = layoutRowRef.current;
    const canvasEl = canvasSlotRef.current;
    if (!row || !canvasEl) return;
    const onChange = (): void => {
      measureRailGeometry();
    };
    const ro = new ResizeObserver(onChange);
    ro.observe(row);
    ro.observe(canvasEl);
    window.addEventListener('resize', onChange);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onChange);
    };
  }, [railDragging, measureRailGeometry, canvasSlotRef]);

  // 全画面 (Fullscreen API)。Classic と同じ共有フックを使い、ヘッダに「全画面」項目を
  // 出す (support 時のみ)。Classic の iPad 注意モーダルは Classic 固有なので Modern は
  // 共有フックの toggle (警告なし) を直に使う。
  const fullscreen = useFullscreen();

  // §9.9 アンチエイリアス (AA)。2026-06-13 (表現パリティ §1.1) で tri-state 化に伴い
  // 所有を共有 chrome (useRoomChrome) へ移設した: 実効値 = chrome.aaEnabled (未設定なら
  // 共通既定 off、consult 2026-06-13 続報でレイアウト別既定 Modern=on を撤廃)、変更 =
  // chrome.onAaChange (明示設定として localStorage 永続 + レイアウト間共有)。
  // ModernToolProperties と PenSettingsModal (ヘッダ「ペン」) が同じ値を読み書きする
  // (= 二重の真実なし)。pen.aa への同期も chrome 側で行う。

  // /bellon roomin (入室お知らせベル)。
  const bellStore = useMemo(() => createBellOnRoominStore(), []);
  const bellSound = useMemo<BellSoundAdapter>(() => createBrowserBellSound(), []);
  const [bellOnRoomin, setBellOnRoominState] = useState<BellOnState>(() => bellStore.read());
  const setBellOnRoomin = (next: BellOnState): void => {
    setBellOnRoominState(next);
    bellStore.write(next);
  };
  const bellOnRoominRef = useRef<BellOnState>(bellOnRoomin);
  bellOnRoominRef.current = bellOnRoomin;
  useEffect(() => {
    const off = transport.onEvent((event) => {
      if (event.t !== 'room.member-joined') return;
      if (bellOnRoominRef.current !== 'on') return;
      bellSound.play();
    });
    return off;
  }, [transport, bellSound]);

  // ChatPanel への命令アクセス (MemberPanel ダブルクリックで名前挿入)。
  const chatPanelRef = useRef<ChatPanelHandle | null>(null);
  const handleMemberDoubleClick = (displayName: string): void => {
    chatPanelRef.current?.insertName(displayName);
  };

  // 初回オンボーディング coachmark (§9.4.1 ②)。
  const onboardingSeenStoreRef = useRef(createLayoutOnboardingSeenStore());
  const [showCoachmark] = useState<boolean>(() =>
    shouldShowOnboardingCoachmark(layout, onboardingSeenStoreRef.current.hasSeen()),
  );
  const markCoachmarkSeen = (): void => {
    onboardingSeenStoreRef.current.markSeen();
  };

  // ツール帯のブラシ/消しゴム選択を engine controller の pen state に橋渡し。
  // chrome.onToggleEraser は eraser ⇄ 直前ペン を切り替えるので:
  //   - ブラシ選択: 今 eraser なら toggle で直前ペンに戻す。非 eraser なら現状維持。
  //   - 消しゴム選択: 今 eraser でないなら toggle で eraser に。
  // ブラシ/消しゴム選択時はスポイト持続モードを必ず解除 (= 相互排他、§9.6)。spuit から
  // 別ツールへ抜けると素クリックが再び描画になる。
  const handleSelectBrush = (): void => {
    chrome.setSpuitMode(false);
    if (chrome.pen.pen === 'eraser') chrome.onToggleEraser();
  };
  const handleSelectEraser = (): void => {
    chrome.setSpuitMode(false);
    if (chrome.pen.pen !== 'eraser') chrome.onToggleEraser();
  };
  // スポイトボタン: 持続モードへ入る。トグル ON (押下で ON、別ツール選択で OFF。再押下で
  // OFF にも対応 = 押し間違いの離脱を許す)。§9.6 方針転換 (2026-06-12)。
  const handleSelectSpuit = (): void => {
    chrome.setSpuitMode(!chrome.spuitMode);
  };

  // conflict-aware Undo (undo-conflict-aware-2026-07-12.md): `chrome.engine`
  // (= CanvasDrawingEngine、§9.7 共有・永続) を渡し、cancel 可能スタック + 拒否通知の
  // 購読と `stroke.cancel` 送信を配線する。名前解決は `state.members` から (自分自身は
  // 「自分」表示 — 2026-07-22「交差対象は自他問わず」で自分の uid が conflicts に入る
  // ケースがあるため)。Classic にも同じフックを個別配線する (2026-07-23 是正、
  // modern-layout.md §1.1 表現パリティ。ModernCommandCluster の常設ボタンだけが
  // Modern 限定、`ClassicLayout.tsx` を参照)。
  const resolveConflictDisplayName = useCallback(
    (uid: string): string | null => {
      if (uid === state.uid) return '自分';
      return state.members.find((m) => m.uid === uid)?.displayName ?? null;
    },
    [state.uid, state.members],
  );
  const undo = useConflictAwareUndo({
    engine: chrome.engine,
    sendCancelStroke,
    sendCancelRegion,
    floatingRegion: {
      isFloating: chrome.region.phase.kind === 'floating',
      cancelFloating: chrome.region.cancel,
    },
    resolveDisplayName: resolveConflictDisplayName,
  });

  // 2026-06-11 キーボードショートカット: Classic と同じ共有フックを配線し、Modern でも
  // 全ショートカットを効かせる (= B(ブラシ)/S(サインペン)/E(消しゴム) ツール・+,-,0 zoom・
  // V flip・Ctrl/Cmd+Enter 発言小窓)。アクションは Classic と同一ソース (chrome.*) を渡す:
  //   - ツール選択は chrome.onSelectBrush / onSelectPen('flat') / onSelectEraserDirect
  //     (= pen を直接 set。ツール帯ボタンの toggle 系 handleSelectBrush とは別経路で、
  //      キーボードは「直接そのツールへ」が原典挙動)。S=サインペンは onSelectPen('flat')。
  //     (旧 M=マーカー bucket toggle は marker 撤去に伴い 2026-06-12 に撤去。)
  //   - zoom は chrome.handleZoomIn / handleZoomOut / handleZoomReset。
  //   - flip (V) は chrome.handleToggleFlip。V キーに加え、ツール帯 (ModernToolRail) の
  //     zoom 近傍に置いた反転トグルボタンも同じ chrome.handleToggleFlip を叩く
  //     (2026-06-12: ナビ窓下部 → ツール帯 view 操作へ移設、CSP のナビゲーター下部
  //     反転クラスタ型を回避)。engine + overlay の flip 配線は CanvasStage に通っている
  //     ので、ボタン / V キーどちらも同一 engine flip 状態をトグルする。
  //   - Ctrl/Cmd+Enter は ModernSocialDock 越しに ChatPanel handle へ転送される
  //     chatPanelRef.openFloatingPopup() を呼ぶ (= 発言小窓は Modern にも存在する)。
  // 2026-06-12 (shortcut-settings.md「Modern Tab 決着」): Tab = 両サイドバー非表示を
  // 配線 (onToggleUiHidden)。Classic の UI 全体隠しと違い Modern は左右 <aside> だけを
  // 畳んで canvas を最大化する (ヘッダは隠さない)。入力欄フォーカス中の Tab スルー
  // (= 通常のフォーカス移動) は useLayoutKeyboardShortcuts 側の shouldSkipBareKey ガード
  // が担保する (素キー window attach)。
  // Space hold pan は別フック (useSpaceHoldPan、上で配線済) の管轄なので重複させない。
  useLayoutKeyboardShortcuts(
    {
      onOpenChatPopup: () => chatPanelRef.current?.openFloatingPopup(),
      onToggleUiHidden: () => setUiHidden((v) => !v),
      // B/S/E キーで描画ツールへ → スポイト持続モードも解除 (= ツール帯ボタンと同じ相互排他)。
      onSelectBrush: () => {
        chrome.setSpuitMode(false);
        chrome.onSelectBrush();
      },
      onSelectSignpen: () => {
        chrome.setSpuitMode(false);
        chrome.onSelectPen('flat');
      },
      onSelectEraserDirect: () => {
        chrome.setSpuitMode(false);
        chrome.onSelectEraserDirect();
      },
      // CSP流 循環ショートカット (2026-08-21、既定キー未割当): 個別ペン直接選択 + タブの
      // 「最後に使ったペンへ」。B/S/E と同じくスポイト持続モードを解除してから選ぶ
      // (= ツール帯ボタンと同じ相互排他)。
      onSelectDot: () => {
        chrome.setSpuitMode(false);
        chrome.onSelectPenRef('dot');
      },
      onSelectTone: () => {
        chrome.setSpuitMode(false);
        chrome.onSelectPenRef('tone');
      },
      onSelectAirbrush: () => {
        chrome.setSpuitMode(false);
        chrome.onSelectPenRef('airbrush');
      },
      onSelectCrayon: () => {
        chrome.setSpuitMode(false);
        chrome.onSelectPenRef('crayon');
      },
      onSelectPastel: () => {
        chrome.setSpuitMode(false);
        chrome.onSelectPenRef('pastel');
      },
      onSelectTexturePen: () => {
        chrome.setSpuitMode(false);
        chrome.onSelectPenRef('texture');
      },
      onSelectTabLine: () => {
        chrome.setSpuitMode(false);
        chrome.onSelectTabRef('line');
      },
      onSelectTabFill: () => {
        chrome.setSpuitMode(false);
        chrome.onSelectTabRef('fill');
      },
      onSelectTabTexture: () => {
        chrome.setSpuitMode(false);
        chrome.onSelectTabRef('texture');
      },
      // I キー: スポイト持続モードへ (= ツール帯ボタンと同じトグル)。Modern 限定。
      onSelectEyedropper: handleSelectSpuit,
      onZoomIn: chrome.handleZoomIn,
      onZoomOut: chrome.handleZoomOut,
      onZoomReset: chrome.handleZoomReset,
      // `f`: 全体表示 (フィット、決定3 §6 consult 2026-09-29)。ツール帯のボタンと
      // 同じ chrome.applyFitZoom を叩く。
      onZoomFit: chrome.applyFitZoom,
      onToggleFlip: chrome.handleToggleFlip,
      // キャンバス表示回転 (canvas-rotation 段取り 2): R = 回転ツール (スポイトと相互排他の
      // 持続ツール、chrome 側で解除を管理)。左右回転 / リセットは既定キー無し。
      onSelectRotateTool: chrome.onSelectRotateTool,
      onRotateLeft: chrome.handleRotateLeft,
      onRotateRight: chrome.handleRotateRight,
      onRotateReset: chrome.handleRotateReset,
      onOpenShortcutSettings: () => setShortcutsOpen(true),
      // §9.10 決定4: `` ` `` でワークスペース preset を巡回 (Tab=focus トグルとは別アクション)。
      onCyclePreset: cyclePreset,
      // Ctrl/Cmd+Z: conflict-aware Undo (`edit.undo` の guardTextInput によりチャット
      // 入力欄フォーカス中は発火しない、registry 側で担保)。
      onUndo: undo.triggerUndo,
      // `[`/`]`: 太さプリセット段階増減 (§9.11、consult 2026-09-29)。登録済み
      // プリセットボタンとは別に、現在値から段階的に増減する (`stepPenWidth`)。
      onWidthStepDown: () => chrome.onWidthChange(stepPenWidth(chrome.pen.width, -1)),
      onWidthStepUp: () => chrome.onWidthChange(stepPenWidth(chrome.pen.width, 1)),
    },
    {
      currentPen: chrome.pen.pen,
      rememberedIndexByBinding: chrome.shortcutCyclePositions,
      onRememberedIndexChange: chrome.onShortcutCycleIndexChange,
    },
  );

  // 最近色 (段階1、consult 2026-06-12): chrome.pen.color (settle した描画色) の履歴を Modern
  // スコープで保持する (= 共有 useRoomChrome は触らない。Classic は最近色を出さない)。
  // ライブ編集スロットが上書きで消した色の救済ネット。クリックで作業スロットへ読込 (=
  // chrome.onColorChange に流す → pen.color に載り、選択スロットへライブ書き戻る)。
  // suppressNextRecord (consult 2026-06-12): 最近色/スロットの「再選択」は既存色の読込で
  // あって新規作成ではないので最近色に積み直さない。下の onRecentSelect / onSelectIdx で
  // 「色変更を発火する直前」に対象色を渡して 1 回抑制する (= 並び不変・読込のみ)。
  // 2026-06-12 スポイト確定の厳密化: スポイト持続モード中 (= chrome.spuitMode) は自動デバウンス
  // 記録を suspend し、ドラッグ中の通過色・静止色を最近色に入れない。確定の1色は DrawingCanvas
  // の onSpuitCommit → chrome.registerSpuitCommitHandler 経由で recordColor が明示記録する。
  // Alt+クリック / 右クリックの瞬間ジェスチャ経路 (= spuitMode 非依存) も同じ onSpuitCommit で
  // 確定の1色を recordColor する。recordColor は lastRecorded を更新するので、確定色が直後に
  // デバウンス経路へ流れても二重記録されない (= noop に倒れる)。非スポイト編集 (カラーホイール
  // 等) は suspended=false のまま従来デバウンス記録を維持する。
  const { recentColors, suppressNextRecord, recordColor } = useRecentColors(chrome.pen.color, {
    suspended: chrome.spuitMode,
  });
  // 確定ハンドラ (= recordColor) を chrome に登録 → DrawingCanvas の onSpuitCommit が転送する。
  const { registerSpuitCommitHandler } = chrome;
  useEffect(() => {
    registerSpuitCommitHandler(recordColor);
    return () => registerSpuitCommitHandler(null);
  }, [registerSpuitCommitHandler, recordColor]);

  // 画像貼付 (permission gate)。
  const imagepasteOnFile = chrome.canImagepaste
    ? (file: File): void => {
        void chrome.imagepaste.start(file);
      }
    : null;

  // タイムラプス X 共有 (T3、growth-timelapse-share-2026-07-02.md §5.3)。
  // 入口は ModernCommandCluster (デスクトップ = コマンドクラスタ / フォン =
  // オーバーフローシートが同クラスタを再ホスト) の 1 箇所。support gate は
  // isTimelapseExportSupported (WebCodecs H.264 実 probe) — 非対応環境では
  // ボタン自体を出さない (disabled でなく非表示、§5.2)。モーダルは両シェル共通
  // (分岐の外、他モーダルと同じ層)。
  const timelapseSupported = useTimelapseExportSupported();
  const [timelapseOpen, setTimelapseOpen] = useState(false);
  // 「New!」バッジ (consult 2026-07-08 案B): 初回にボタンを押したら消す。seen フラグは
  // localStorage 永続 (Classic / Modern 共有 key)。押下経路 (handleTimelapseEntryClick) で
  // markSeen + state 反映するので、押した直後にバッジが消える。
  const timelapseSeenStoreRef = useRef(createTimelapseShareSeenStore());
  const [timelapseSeen, setTimelapseSeen] = useState<boolean>(() =>
    timelapseSeenStoreRef.current.hasSeen(),
  );
  // 2026-07-08 方針変更: 絵ログ入口は **全員に見せる** (flag+support のみで gate)。押下時に
  // オーナー判定で分岐: オーナーは絵ログモーダルを開き、非オーナーは「ルーム設定」と同じ流儀で
  // RoomSettingsModal を開く (adminToken 無し → UnauthenticatedNotice の「ログイン画面へ」で
  // /login へ誘導 = room-settings と同一遷移)。履歴 DL は owner 認証必須のままなので同意なし
  // 公開は抑止される。どちらの押下でも New! バッジは seen 化する。
  const handleTimelapseEntryClick = useCallback((): void => {
    timelapseSeenStoreRef.current.markSeen();
    setTimelapseSeen(true);
    if (isOwner) {
      setTimelapseOpen(true);
    } else {
      setSettingsOpen(true);
    }
  }, [isOwner]);

  // 過去キャンバス一覧 (Slice 2、past-canvas-new-document-2026-07-05.md §4.2/§7)。
  // 入口はコマンドクラスタ (デスクトップ = クラスタ / フォン = オーバーフローシートが
  // 再ホスト) の 1 箇所。絵ログと同じ「全員表示 + 非オーナーはログイン誘導」。押下で
  // オーナー分岐 (一覧モーダル / 設定モーダル誘導)。New! バッジは別 key の seen store。
  const [pastCanvasOpen, setPastCanvasOpen] = useState(false);
  // 荒らし復旧 UI「?recover=<seq>」深リンク (vandalism-recovery-ui-2026-09-27.md
  // §2「入口を『続きを描く』に統一」): 一覧モーダルを開いた時点の seq を保持し、
  // `PastCanvasListModal` の `initialRecoverSeq` に橋渡しする (ClassicLayout と同じ流儀)。
  const [pastCanvasRecoverSeq, setPastCanvasRecoverSeq] = useState<number | undefined>(undefined);
  const pastCanvasSeenStoreRef = useRef(createPastCanvasSeenStore());
  const [pastCanvasSeen, setPastCanvasSeen] = useState<boolean>(() =>
    pastCanvasSeenStoreRef.current.hasSeen(),
  );
  const handlePastCanvasEntryClick = useCallback((): void => {
    pastCanvasSeenStoreRef.current.markSeen();
    setPastCanvasSeen(true);
    if (isOwner) {
      setPastCanvasOpen(true);
    } else {
      setSettingsOpen(true);
    }
  }, [isOwner]);
  // mount 時に深リンクの seq があれば一覧モーダルを自動で開く (ClassicLayout と同じ流儀)。
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount 時に一度だけ消費する (initialRecoverSeq は App.tsx 側で 1 回しか設定されない)。
  useEffect(() => {
    if (initialRecoverSeq === undefined) return;
    setPastCanvasRecoverSeq(initialRecoverSeq);
    setPastCanvasOpen(true);
    onRecoverDeepLinkConsumed?.();
  }, []);

  // 使い方ボタン (作者フィードバック 2026-09-22「もっと目立つようにできないかな」):
  // 初回クリックまで「New!」バッジを出す。Classic と同じ store key (= どちらの入口
  // で押しても両方消える)。押下は owner 分岐せず、seen 化 + 別タブ遷移のみ。
  const helpSeenStoreRef = useRef(createHelpButtonSeenStore());
  const [helpSeen, setHelpSeen] = useState<boolean>(() => helpSeenStoreRef.current.hasSeen());
  const handleHelpEntryClick = useCallback((): void => {
    helpSeenStoreRef.current.markSeen();
    setHelpSeen(true);
    window.open(buildHelpUrl('modern'), '_blank', 'noopener,noreferrer');
  }, []);

  // S3 スライス2 (floating-panels-2026-08-01.md §7): `/open tool` (旧 Java 版
  // `/open tool` パリティ回復コマンド) を ChatPanel の共有 slash command 経路
  // (`application/slashCommand.ts`) から呼ぶ。Modern は S2 で既にフロート機構を
  // 持つので、colorPalette (= パレット/カラー系パネルの代表) を `floatPanelLive`
  // で切り離すだけで良い (Classic 側は専用の toolFloat state で同じ効果を作る)。
  // 既にフロート中なら idempotent (= 前面化だけ行う、二重 float しない)。
  const handleOpenToolFloat = useCallback((): void => {
    if (isPanelFloating(workspace, 'colorPalette')) {
      setFloatOrder((order) => bringToFront(order, 'colorPalette'));
      return;
    }
    const landing = clampFloatPosition(
      { x: 140, y: 120 },
      floatSizeFor('colorPalette'),
      viewportSizeNow(),
      FLOAT_EDGE_INSET_PX,
    );
    commitLive(floatPanelLive(live, 'colorPalette', landing));
    setFloatOrder((order) => bringToFront(order, 'colorPalette'));
  }, [workspace, live, commitLive, floatSizeFor]);

  // §12 (floating-panels-2026-08-01.md): `/close tool` / `/open chat` /
  // `/close chat`。redock の region 決定は「⇤ドック」ボタンと**同じ位置ベース
  // resolver** (`floatPanelSide` → `edgeSnapRegion` ?? `homeRegionOf`) を再利用する
  // (= 判定系を二重に作らない、FloatShell の `onReturnToDock` と同じロジック)。
  const redockFloatingPanel = useCallback(
    (id: PanelId): void => {
      if (!isPanelFloating(workspace, id)) return;
      const position = workspace.floating?.[id];
      if (!position) return;
      const side = floatPanelSide(position, floatSizeFor(id), viewportSizeNow());
      const region = edgeSnapRegion(workspace, side) ?? homeRegionOf(id);
      commitLive(movePanelLive(live, id, region, workspace.panels[region].length));
    },
    [workspace, live, commitLive, floatSizeFor],
  );
  const handleCloseToolFloat = useCallback((): void => {
    redockFloatingPanel('colorPalette');
  }, [redockFloatingPanel]);
  // `/open chat`: Classic は既存の chatDetached にそのまま繋ぐだけだが、Modern に
  // 「chatDetached」相当の独立 state は無い (= socialDock は元々 S2 のフロート機構に
  // 乗っている)。よって `/open tool` と同じ経路 (floatPanelLive) で socialDock を
  // 浮かせる。既にフロート中なら idempotent (前面化のみ)。
  const handleOpenChatFloat = useCallback((): void => {
    if (isPanelFloating(workspace, 'socialDock')) {
      setFloatOrder((order) => bringToFront(order, 'socialDock'));
      return;
    }
    const landing = clampFloatPosition(
      { x: 200, y: 160 },
      floatSizeFor('socialDock'),
      viewportSizeNow(),
      FLOAT_EDGE_INSET_PX,
    );
    commitLive(floatPanelLive(live, 'socialDock', landing));
    setFloatOrder((order) => bringToFront(order, 'socialDock'));
  }, [workspace, live, commitLive, floatSizeFor]);
  const handleCloseChatFloat = useCallback((): void => {
    redockFloatingPanel('socialDock');
  }, [redockFloatingPanel]);

  // 2026-06-12 共有ヘッダ項目 (RoomHeader): Classic と **同じ全項目・同じ順序・同じ
  // scope/align/aria 意味論** で組み立てる (= 帯の見た目・項目を完全一致化、ユーザ確定)。
  // 各項目の onClick だけ Modern の handler に差し替わる。
  //   左クラスタ: 全画面 (support 時のみ) → 表示
  //   右クラスタ (align: 'end'): ルーム設定 → 退室
  const modernHeaderItems: ReadonlyArray<RoomHeaderItem> = [
    ...(fullscreen.supported
      ? ([
          {
            kind: 'action',
            id: 'fullscreen',
            label: fullscreen.isFullscreen ? '元に戻す' : '全画面',
            onClick: fullscreen.toggle,
            pressed: fullscreen.isFullscreen,
            scope: 'session',
            manualId: 'view.fullscreen',
          },
        ] as const)
      : []),
    {
      kind: 'action',
      id: 'view-prefs',
      label: '表示',
      onClick: () => setUiPrefsOpen(true),
      title: '表示の設定 (チャット欄の文字の大きさなど)',
      opensDialog: true,
      scope: 'personal',
      manualId: 'layout.ui-scale',
    },
    {
      // §9.10 (consult 2026-06-16 第2スライス) ワークスペース preset ピッカー。第1スライスの
      // 単独「UI左右入替」トグルを廃し、curated な Tier1 preset (標準 / 左右入替 / お絵描き集中 /
      // チャット重視) を選ぶ select に置換した (§9.10 決定3/4)。verb 名
      // 「UI左右入替」は purpose 名 `左右入替` preset に吸収。§9.4.1①「両レイアウト共通 chrome
      // の設定内」流儀に寄せ personal-scope 相当の自分用 UI として置く (= action ボタンに
      // 収まらないので RoomHeader の custom slot)。`` ` `` キー cycle と同じ 1 本の state。
      kind: 'custom',
      id: 'workspace-preset',
      node: (
        // §9.10 第3スライス: 配置ピッカー (preset/カスタム) の隣に「パネル」表示メニューを
        // 並べる (= discoverable な復帰経路。隠したパネルもここで戻せる)。ピッカーの value は
        // live から導出 (custom 編集中は「カスタム」を指す)。
        <div className="flex shrink-0 items-center gap-1">
          <ModernWorkspacePresetPicker
            presets={WORKSPACE_PRESETS}
            value={pickerValue(live)}
            onChange={setPreset}
          />
          <ModernPanelVisibilityMenu items={panelToggleItems} onToggle={handleTogglePanel} />
        </div>
      ),
    },
    {
      // 2026-06-13 続報 (consult): 描画ツール挙動の設定。Classic と同一の独立
      // ボタン (= 同じ topBarItems の流儀に乗せて特別扱いしない)。ModernToolProperties
      // の既存トグルと同じ共有 chrome 値の別ビュー。
      // 2026-06-27 (consult): ブラシカーソル / スポイトの取得元も束ねるため「ペン」→「描画」へ改名。
      kind: 'action',
      id: 'pen-settings',
      label: '描画',
      onClick: () => setPenSettingsOpen(true),
      title: '描画の設定 (手ぶれ補正 / アンチエイリアス / ブラシカーソル / スポイトの取得元)',
      opensDialog: true,
      scope: 'personal',
    },
    {
      // 2026-06-12 ショートカット設定 (shortcut-settings.md スライス2): Classic と
      // 同一の独立ボタン。`?` ホットキーでも同モーダルを開く (layout 非依存)。
      kind: 'action',
      id: 'shortcut-settings',
      label: 'ショートカット',
      onClick: () => setShortcutsOpen(true),
      title: 'ショートカットキーの一覧・変更 (?)',
      opensDialog: true,
      scope: 'personal',
      manualId: 'shortcut.list',
    },
    // 使い方マニュアル (作者決定 2026-09-22、目立たせ改修): Classic と同一の位置
    // (「ショートカット」の隣、左寄せ群の最後)。別タブで www の `/help/?env=modern` を
    // 開く。マニュアル項目としては扱わない (= data-manual を付けない、
    // manualRegistry.ts の登録対象外)。他ボタンと違うティール系アクセント
    // (scope:'help') + 丸囲み HelpIcon (Classic は "?" 文字プレフィクスで代替) +
    // 初回のみ「New!」バッジ (helpSeen、Classic と同じ store key)。
    {
      kind: 'action',
      id: 'help',
      label: '使い方',
      onClick: handleHelpEntryClick,
      title: '使い方マニュアルを別タブで開く',
      scope: 'help',
      icon: <HelpIcon className="h-3.5 w-3.5" />,
      badge: !helpSeen,
    },
    // 新機能の紹介 (whats-new-2026-10-15.md): Classic と同じ位置・同じ文言。自動では
    // 開かず、未読があるときだけ控えめなドット。
    {
      kind: 'action',
      id: 'whats-new',
      label: '新機能',
      onClick: whatsNew.openDialog,
      title: '新機能の紹介を見る',
      opensDialog: true,
      unreadDot: whatsNew.hasUnread,
    },
    // 招待 (room-invite-2026-10-01.md): 部屋 URL の表示 / コピー / 共有 / QR。左寄せ群の
    // 最後 (= 部屋名の近く) に常設。
    {
      kind: 'action',
      id: 'invite',
      label: '友達を招待',
      onClick: inviteDialog.openDialog,
      title: '部屋の URL を友達に送る (コピー / 共有 / QR)',
      opensDialog: true,
      scope: 'invite',
      icon: <InviteIcon className="h-3.5 w-3.5" />,
    },
    {
      kind: 'action',
      id: 'room-settings',
      label: 'ルーム設定',
      onClick: () => setSettingsOpen(true),
      title: isOwner
        ? 'ルーム名・公開設定・オーナーパスワードなど'
        : 'オーナーはここからログイン (ルーム名・公開設定など)',
      opensDialog: true,
      align: 'end',
      scope: 'owner',
      manualId: 'room.settings',
    },
    {
      kind: 'action',
      id: 'leave',
      label: '退室',
      onClick: leave,
      align: 'end',
      title: 'ルームから退室',
      scope: 'session',
      manualId: 'start.leave',
    },
  ];

  // §9.10 構造化レイアウト state (consult 2026-06-14): 9 パネルを「id → render
  // クロージャ」のレジストリで表す。render はこの ModernLayout が持つローカル
  // state/handler (suppressNextRecord / recentColors / panToggleOn 等) を捕捉する
  // (= 設計論点「ローカル state 依存の手配線を純 id→component map に落としきらない」の
  // 解。配置 (どの region に・どの順) だけ workspaceLayout でデータ化し、手配線は
  // render 側に残す)。レジストリの定義順 = カラーパネル→16色、プレビュー→レイヤー→社交
  // を保ち、各パネルの中身・props は移設前と一字一句同一 (= 無退行)。
  const panelRegistry: Readonly<Record<PanelId, () => React.ReactNode>> = {
    // §9.10 第4スライス: 余り高さ吸収 ([&>div]:h-full / flex-1) は ModernPanelDragShell の
    // grow が担うようになった (= どの region に来ても伸び方が一定)。ここはツール帯本体だけ。
    toolRail: () => (
      <ModernToolRail
        pen={chrome.pen.pen}
        onSelectBrush={handleSelectBrush}
        onSelectEraser={handleSelectEraser}
        spuitActive={chrome.spuitMode}
        onSelectSpuit={handleSelectSpuit}
        regionActive={chrome.regionToolActive}
        onSelectRegion={chrome.onSelectRegionTool}
        showRegionNewBadge={chrome.showRegionNewBadge}
        panActive={panToggleOn}
        onTogglePan={() => setPanToggleOn((v) => !v)}
        zoom={chrome.zoom}
        zoomMin={ZOOM_FIT_MIN}
        zoomMax={ZOOM_MAX}
        onZoomIn={chrome.handleZoomIn}
        onZoomOut={chrome.handleZoomOut}
        onZoomFit={chrome.applyFitZoom}
        onZoomReset={chrome.handleZoomReset}
        flipX={chrome.flipX}
        onToggleFlip={chrome.handleToggleFlip}
        rotation={chrome.rotation}
        onRotateReset={chrome.handleRotateReset}
        rotateToolActive={chrome.rotateToolActive}
        onSelectRotateTool={chrome.onSelectRotateTool}
        // custom-cursor-ux フォローアップ (2026-09-19、§A-2): ツール帯の補助入口
        // (自分のカーソルのサムネ / 未作成なら「＋」の点線枠アイコン).
        selfCursor={selfCursor}
        cursorEditorOpen={cursorEditorOpen}
        onOpenCursorEditor={openCursorEditor}
        showCursorNewBadge={!cursorNewBadgeSeen}
        showRotateNewBadge={!rotateNewBadgeSeen}
      />
    ),
    commandCluster: () => (
      <ModernCommandCluster
        canClear={chrome.canClear}
        onClear={chrome.handleClear}
        canUndo={undo.canUndo}
        onUndo={undo.triggerUndo}
        showUndoBadge={undo.showBadge}
        onCapture={chrome.handleCapture}
        onImagepasteFile={imagepasteOnFile}
        nameLabelMode={chrome.nameLabelMode}
        onToggleNameLabelMode={chrome.handleToggleNameLabelMode}
        onShareTimelapse={
          // 2026-07-08 方針変更 (room-settings 流儀): 絵ログ入口は全員に見せ、非オーナーが
          // 押したらオーナーログインへ誘導する。gate は support/flag のみ (isOwner を外す)。
          // null は WebCodecs 非対応 or flag off のときだけ (= ボタン非表示)。
          TIMELAPSE_SHARE_ENABLED && timelapseSupported ? handleTimelapseEntryClick : null
        }
        // 押せるのがオーナー本人か (tooltip/aria の出し分けに使う。非オーナーはログイン誘導文言)。
        timelapseShareOwner={isOwner}
        // 初回のみ「New!」バッジ (押すと handleTimelapseEntryClick が seen 化して消える)。
        showTimelapseBadge={!timelapseSeen}
        // 過去キャンバス入口 (Slice 2)。全員表示、押下でオーナー分岐 (一覧 / ログイン誘導)。
        onOpenPastCanvas={handlePastCanvasEntryClick}
        pastCanvasOwner={isOwner}
        showPastCanvasBadge={!pastCanvasSeen}
      />
    ),
    // 常設「ブラシ」パネル (modern-brush-panel-2026-08-17.md §3/§4/§14): 「どのブラシか」
    // (= 旧 PenKindPicker チップ) はここに集約。「そのブラシをどう調整するか」は
    // 引き続き toolProperties 側 (PenParamsSection)。
    // **isMobile 分岐 (§14 実装注記)**: デスクトップはタブ構成 (`ModernBrushPanel`)、
    // フォンはタブを持ち込まず全ブラシを並べる旧実装のまま (`ModernBrushPanelFlat`、
    // `ModernPhoneShell` のボトムシートは既に 2 段ナビなのでタブを足すと悪化するため)。
    // props は両者で同一 (`ModernBrushPanelProps`、brushPanelShared.ts 共有)。
    brushPanel: () =>
      isMobile ? (
        <ModernBrushPanelFlat
          pen={chrome.pen.pen}
          onSelectPen={chrome.onSelectPen}
          onSelectTexturePreset={chrome.onSelectTexturePreset}
          width={chrome.pen.width}
          alpha={chrome.pen.alpha}
          pressure={chrome.currentPressure}
          texturePreset={chrome.texturePreset}
          airbrushGrain={chrome.airbrushGrain}
          airbrushDensity={chrome.airbrushDensity}
        />
      ) : (
        <ModernBrushPanel
          pen={chrome.pen.pen}
          onSelectPen={chrome.onSelectPen}
          onSelectTexturePreset={chrome.onSelectTexturePreset}
          width={chrome.pen.width}
          alpha={chrome.pen.alpha}
          pressure={chrome.currentPressure}
          texturePreset={chrome.texturePreset}
          airbrushGrain={chrome.airbrushGrain}
          airbrushDensity={chrome.airbrushDensity}
        />
      ),
    toolProperties: () => (
      <ModernToolProperties
        pen={chrome.pen.pen}
        width={chrome.pen.width}
        onWidthChange={chrome.onWidthChange}
        widthPresets={widthPresets}
        onAddWidthPreset={handleAddWidthPreset}
        onRemoveWidthPreset={handleRemoveWidthPreset}
        onResetWidthPresets={handleResetWidthPresets}
        alpha={chrome.pen.alpha}
        onAlphaChange={chrome.onAlphaChange}
        texturePreset={chrome.texturePreset}
        onSelectTexturePreset={chrome.onSelectTexturePreset}
        toneDensity={chrome.toneDensity}
        onToneDensityChange={chrome.onToneDensityChange}
        tonePitch={chrome.tonePitch}
        onTonePitchChange={chrome.onTonePitchChange}
        airbrushGrain={chrome.airbrushGrain}
        onAirbrushGrainChange={chrome.onAirbrushGrainChange}
        airbrushDensity={chrome.airbrushDensity}
        onAirbrushDensityChange={chrome.onAirbrushDensityChange}
        crayonGrain={chrome.crayonGrain}
        onCrayonGrainChange={chrome.onCrayonGrainChange}
        pastelGrain={chrome.pastelGrain}
        onPastelGrainChange={chrome.onPastelGrainChange}
        dotSpacing={chrome.dotSpacing}
        onDotSpacingChange={chrome.onDotSpacingChange}
        pressure={chrome.currentPressure}
        onPressureChange={chrome.onPressureChange}
        penOnlyMode={chrome.penOnlyMode}
        onPenOnlyModeChange={chrome.setPenOnlyMode}
        detailsOpen={toolDetailsOpen}
        onDetailsToggle={setToolDetailsOpen}
        stabilizerLevel={chrome.stabilizerLevel}
        onStabilizerChange={chrome.onStabilizerChange}
        aaEnabled={chrome.aaEnabled}
        onAaChange={chrome.onAaChange}
        dotByDotMode={chrome.dotByDotMode}
        onDotByDotModeChange={chrome.onDotByDotModeChange}
        dotByDotActive={chrome.dotByDotActive}
        mask={chrome.pen.mask}
        slot1={chrome.slot1}
        slot2={chrome.slot2}
        maskTypeUI={chrome.maskTypeUI}
        onMaskSlotClick={chrome.onMaskSlotClick}
        onMaskTypeToggle={chrome.onMaskTypeToggle}
        spuitSampleMode={chrome.spuitSampleMode}
        onSpuitSampleModeChange={chrome.onSpuitSampleModeChange}
      />
    ),
    // カラーパネル (カラーホイール)。不透明度操作はツールプロパティ側へ移したので、
    // alpha255 はスウォッチ表示専用に渡す (onAlphaChange は渡さない、consult 2026-06-08)。
    colorPanel: () => (
      <ModernColorPanel
        value={{ rgb24: chrome.pen.color, alpha255: chrome.pen.alpha }}
        onColorChange={chrome.onColorChange}
      />
    ),
    // カラーホイール直下の旧版16色ライブ編集スロット + 最近色 (consult 2026-06-12)。
    // onSelectIdx / onRecentSelect は suppressNextRecord (ローカル state) を捕捉する
    // 手配線 (= §9.10 設計論点。純 map に落とさず render クロージャで保持)。
    colorPalette: () => (
      <ModernColorPalette
        palette={chrome.paletteSlots}
        selectedIdx={chrome.selectedPaletteIdx}
        onSelectIdx={(idx) => {
          // スロット選択は既存色の読込 (= 新規作成ではない)。読込で pen.color が
          // 変わっても最近色には積まないよう、選択スロット色を抑制してから選択する。
          const c = chrome.paletteSlots[idx];
          if (c !== undefined) suppressNextRecord(c);
          chrome.onPaletteSlotSelect(idx);
        }}
        recentColors={recentColors}
        onRecentSelect={(c) => {
          // 最近色クリックは作業スロットへの読込のみ。最近色リストは不変
          // (= 積み直さない/並べ替えない) ので、変更発火の直前に抑制する。
          suppressNextRecord(c);
          chrome.onColorChange(c);
        }}
        // α feedback (2026-06-13): パレットを初期16色に戻す。ConfirmDialog は
        // ModernColorPalette 内で自前管理 (Classic PalettePanel と同じ分担)。
        onReset={chrome.onPaletteReset}
      />
    ),
    // 中央 canvas 空スロット (§9.7)。CanvasStage がこの矩形に absolute で重なる。
    // region 'canvas' は左右どちらの aside にも入らず、中央独立 div として出す。
    canvas: () => <div ref={canvasSlotRef} data-canvas-slot className="min-h-0 min-w-0 flex-1" />,
    previewPanel: () => (
      <ModernPreviewPanel
        canvasRef={chrome.canvasRef}
        wrapperRef={canvasScrollRef}
        // 2026-09-29 viewport transform 移行 (決定5): pan 位置は chrome
        // (useRoomChrome) が single source of truth で持つ `{tx,ty}`。
        viewMapper={chrome.viewMapper}
        onSetViewport={chrome.setViewport}
        canvasWidth={state.canvas.width}
        canvasHeight={state.canvas.height}
        zoom={chrome.displayScale}
        flipX={chrome.flipX}
        // 2026-06-13 (feature/preview-perf): 自分のストローク中 / パン中はサムネ再生成を
        // サスペンド (= カーソルのフレーム予算を守る)。
        isSuspended={() => isLocalInteractionActive(chrome.isStrokeActive(), chrome.panActive)}
        // 2026-09-28 (feature/preview-panel-background): サムネにも部屋の背景色を反映
        // (DrawingCanvas と同じ chrome state)。
        backgroundVisible={chrome.backgroundVisible}
        backgroundRgb={chrome.backgroundRgb}
      />
    ),
    layerPanel: () => (
      <ModernLayerPanel
        activeLayerId={chrome.pen.layerId}
        visibility={chrome.layerVisibilityMap}
        onSelectActive={chrome.handleSelectActiveLayer}
        onToggleVisible={chrome.handleToggleLayerVisible}
        opacities={chrome.layerOpacities}
        opacityControl={chrome.layerOpacityControl}
        thumbnails={chrome.layerThumbnailHub}
        backgroundVisible={chrome.backgroundVisible}
        onToggleBackgroundVisible={chrome.handleToggleBackgroundVisible}
        backgroundRgb={chrome.backgroundRgb}
        canSetBackground={chrome.canSetBackground}
        onToggleBackgroundColorWheel={() => setBgColorWheelOpen((o) => !o)}
        regionRoles={chrome.region.roles}
      />
    ),
    socialDock: () => (
      <ModernSocialDock
        chatRef={chatPanelRef}
        chat={view.chat}
        system={view.system}
        selfUid={state.uid}
        isRom={state.isRom}
        onSendChat={sendChat}
        fontScaleEm={chatFontEm}
        slashPorts={{
          pushSystemLine,
          setRom,
          currentIsRom: state.isRom,
          clearChatLog,
          imagepasteOnFile,
          currentBellOnRoomin: bellOnRoomin,
          setBellOnRoomin,
          openToolFloat: handleOpenToolFloat,
          closeToolFloat: handleCloseToolFloat,
          openChatFloat: handleOpenChatFloat,
          closeChatFloat: handleCloseChatFloat,
        }}
        members={state.members}
        selfPermissions={state.permissions}
        filteredUids={filteredUids}
        onKick={({ uid, displayName }) =>
          sendKick({ targetUid: uid, reason: `kick from member list (${displayName})` })
        }
        onToggleFilter={toggleFilter}
        onMemberDoubleClick={handleMemberDoubleClick}
        cursorDrafts={view.cursorDrafts}
        // custom-cursor-ux フォローアップ (2026-09-19): ソーシャルドック上部の単独
        // テキストボタンは廃止し、MemberPanel の自分の行 (サムネ / 「＋」) を主導線に
        // 一本化した (§A-2)。ModernSocialDock は素通しするだけ.
        onOpenCursorEditor={openCursorEditor}
        showCursorEditorHint={!cursorHintSeen}
      />
    ),
  };

  // §9.10 第4スライス: 列内で残り高さを占有するパネル (= 移設前の挙動を維持)。toolRail は
  // 旧 `[&>div]:h-full` ラッパ、socialDock は自前 flex-1 で伸びていた。これを「パネル属性」
  // として 1 箇所に集約し、どの region に来ても (DnD で移動しても) 同じ伸び方をさせる
  // (= ドラッグシェルの grow が担う)。これ以外のパネルは自然高さ (shrink-0)。
  const GROW_PANELS: ReadonlySet<PanelId> = new Set<PanelId>(['toolRail', 'socialDock']);

  // §9.10 第4スライス: region 別ラッパの switch ハードコード (= 第1スライス残課題) を廃し、
  // **汎用の column コンテナ + パネル単位の ModernPanelDragShell** に一般化する。どのパネルが
  // どの region に来ても (= DnD で移動しても) 描けるようにし、並べ替え/ドック間移動を可能に
  // した。各パネルは grip 帯付きのドラッグシェルで包む (= 掴み所・ドロップ挿入線)。
  //
  // 旧 region 別の見た目差 (leftRail=shrink-0 列 / toolColumn=flex-1 列 / rightDock=column・
  // socialDock が flex-1) は (a) 列コンテナ共通化 + (b) パネルの grow 属性 に分解されたので、
  // 無退行のまま統一できた。空になった region は中身が空のコンテナになるだけ (= aside 自体は
  // isAsideEmpty で出し分け)。canvas region はここを通らない (= 中央独立、下で別出し)。
  const renderRegion = (region: WorkspaceRegion): React.ReactNode => {
    // §9.10 第2スライス: 非表示 (hidden / 畳み) パネルは描かない。canvas は常に表示。
    const ids = visiblePanels(workspace, region);
    // 列コンテナの幅挙動 (= 旧 region 別差の唯一の残り): leftRail (= ツール帯 icon 列) だけは
    // 内容幅に hug (shrink-0)、他 (toolColumn / rightDock) は aside の残り幅/高さを占有
    // (flex-1)。これは「アイコン列は細く・カラー/ドックは広く」という列の役割で、パネルの
    // 出入り (DnD 移動) では変わらない (= region の属性として 1 行で表す)。
    const fillColumn = region !== 'leftRail';
    // ドラッグ中はこの列の末尾余白も drop 候補にし、列末尾 (index = ids.length) への
    // 挿入 / 空 region への移動を可能にする。S1: `data-end-index` に焼き込んで
    // `collectPanelDropCandidates` (DOM 計測) が読む (= 旧ネイティブ dragover の
    // バブリングフォールバックを pointermove ヒットテストへ置き換えた)。
    return (
      <div
        key={region}
        data-region={region}
        data-end-index={ids.length}
        className={`flex min-h-0 min-w-0 flex-col gap-2 ${fillColumn ? 'flex-1' : 'shrink-0'}`}
      >
        {ids.map((id, index) => (
          <ModernPanelDragShell
            key={id}
            panelId={id}
            region={region}
            index={index}
            label={PANEL_ARIA[id]}
            grow={GROW_PANELS.has(id)}
            draggingId={draggingId}
            dropTarget={dropTarget}
            onPointerDragStart={handlePanelDragStart}
            onPointerDragMove={handlePointerDragMove}
            onPointerDragEnd={handlePanelDragEnd}
          >
            {panelRegistry[id]()}
          </ModernPanelDragShell>
        ))}
      </div>
    );
  };

  // order を canvas で分割し、左 aside / 右 aside に入る region 群を得る (mirror なら
  // order が反転しているので左右が入れ替わる)。canvas は中央独立スロットとして別出し。
  const { left: leftRegions, right: rightRegions } = splitAroundCanvas(workspace);

  // §9.10 第2スライス a11y 修正: aside の aria-label を **実内容から導出**する。
  // 第1スライスは "ツール" / "プレビュー / レイヤー / チャット" を静的に左右へ固定して
  // いたため、mirror (左右入替) でパネル列が入れ替わるとラベルと中身が食い違っていた
  // (verify で発見)。region 群に含まれる主要パネルから人間可読ラベルを組み立てる。
  // PANEL_ARIA は live state 直後で定義済 (= パネル表示メニューと同一語彙を共有)。
  const asideAriaLabel = (regions: ReadonlyArray<WorkspaceRegion>): string => {
    // 表示中パネルのラベルを order 順に拾い重複を畳む (= 中身に追従)。空なら汎用語。
    const labels: string[] = [];
    for (const region of regions) {
      for (const id of visiblePanels(workspace, region)) {
        const l = PANEL_ARIA[id];
        if (!labels.includes(l)) labels.push(l);
      }
    }
    return labels.length > 0 ? labels.join(' / ') : 'パネル';
  };
  const leftAriaLabel = asideAriaLabel(leftRegions);
  const rightAriaLabel = asideAriaLabel(rightRegions);
  // aside 群が全パネル非表示なら aside 自体を出さない (= canvas がその分広がる)。
  const leftAsideVisible = leftRegions.length > 0 && !isAsideEmpty(workspace, leftRegions);
  const rightAsideVisible = rightRegions.length > 0 && !isAsideEmpty(workspace, rightRegions);

  // 第5スライス (レール端ドック): aside が leftRail だけで成るときは固定ドック幅 (244px) を
  // 与えず **hug 幅** で描く (= レールがスリムのまま)。leftInner (toolColumn と同居) は通常
  // ツール群幅で、レール自体は renderRegion の shrink-0 で細い。
  const leftRailOnly = isRailOnlyAside(workspace, leftRegions);
  const rightRailOnly = isRailOnlyAside(workspace, rightRegions);
  // aside の flex 方向: region を横並びにして leftRail (= shrink-0 の細い列) を隣の region
  // (toolColumn / rightDock) の **横** に立たせる (= 端ドックスリム strip)。従来 rightDock を
  // 含む aside は flex-col だったが、これは「単一 region (rightDock) のとき方向が無意味」だった
  // ためで、第5スライスで leftRail が rightDock と同居 (rightInner/rightOuter スロット) すると
  // flex-col では rail が dock の上に縦積みされてしまう。leftRail を含む aside は必ず flex-row
  // (= rail strip を横に)、leftRail を含まない rightDock-only aside のみ従来どおり flex-col。
  const asideFlexDir = (regions: ReadonlyArray<WorkspaceRegion>): string =>
    regions.includes('rightDock') && !regions.includes('leftRail') ? 'flex-col' : '';
  // aside の inline style 幅: rail-only は undefined (= width 指定なし → 内容 hug)。
  const leftAsideWidth = leftRailOnly ? undefined : asideWidthPx(workspace, leftRegions);
  const rightAsideWidth = rightRailOnly ? undefined : asideWidthPx(workspace, rightRegions);
  // 現在のレールスロット (= ドラッグ中インジケータの「現在地」薄表示 / a11y)。
  const railSlotNow = currentRailSlot(workspace);

  return (
    <>
      {/* M1-b シェル分岐 (modern-phone-shell-2026-07-02.md §3.1): スマホ (isMobile =
          isPhoneViewport: 幅 < 640 ‖ coarse × 短辺 < 640、resize / matchMedia 追従 —
          横持ちでもフォン維持) では ModernPhoneShell (全画面 canvas + 下部バー +
          ボトムシート) を、それ以外は従来のデスクトップシェルを描く。state / handler /
          panelRegistry は上 (このコンポーネント) が保持し両シェルで共有する。canvas は
          どちらのシェルも空スロット div (panelRegistry.canvas) を出すだけで、CanvasStage
          (RoomLayout 直下常設) は分岐の影響を受けない (= 境界 resize でも engine 無 remount、
          §9.7 不変)。モーダル群 (ConfirmDialog / 設定各種) は分岐の外で両シェル共通。 */}
      {isMobile ? (
        <ModernPhoneShell
          roomLabel={state.roomName || state.roomId.slice(0, 8)}
          memberCount={state.members.length}
          connected={view.phase.t === 'joined'}
          pen={chrome.pen.pen}
          penColorRgb24={chrome.pen.color}
          layerId={chrome.pen.layerId}
          onSelectBrush={handleSelectBrush}
          onSelectEraser={handleSelectEraser}
          regionActive={chrome.regionToolActive}
          onSelectRegion={chrome.onSelectRegionTool}
          showRegionNewBadge={chrome.showRegionNewBadge}
          onLeave={leave}
          onOpenSettings={() => setSettingsOpen(true)}
          onHelpClick={handleHelpEntryClick}
          onInviteClick={inviteDialog.openDialog}
          onWhatsNewClick={whatsNew.openDialog}
          whatsNewUnread={whatsNew.hasUnread}
          rotation={chrome.rotation}
          onRotateReset={chrome.handleRotateReset}
          helpSeen={helpSeen}
          panels={panelRegistry}
          retentionChip={
            <RoomRetentionChip
              roomId={state.roomId}
              adminToken={adminTokens.get(state.roomId) ?? undefined}
              onOpenSettings={() => setSettingsOpen(true)}
            />
          }
        />
      ) : (
        <div className="relative flex h-full flex-col bg-[var(--color-bg)]">
          {/* メンテ予告 / お知らせバナーと入室お知らせモーダルは 2026-06-14 に RoomLayout
            (App.tsx) 直下へ hoist した (= Classic/Modern 共有層)。layout 切替で chrome が
            remount しても dismiss 状態 (memory only) が保持され、× で閉じたバナー/モーダル
            が復活しなくなる。banner は RoomLayout root を anchor に absolute top-* で出るので
            見た目・stacking は不変。ここでは mount しない。 */}

          {/* 2026-06-12 共有 RoomHeader (§9.4.1 ① 置き場所 A): Classic と完全一致の帯。
            項目 = 全画面 / 表示 / ルーム設定 / 退室 (Classic と同じ全項目・順序・scope)。
            ルーム名は左寄せ。layoutToggle は右端クラスタ手前 (= Classic と同位置)。
            ルーム設定は Classic にもある同じ RoomSettingsModal を配線 (consult 2026-06-08)。
            notice = Modern 限定の「超テスト版」注意書き (帯中央の琥珀バッジ、2026-06-13)。 */}
          {/* biome-ignore format: smoke test が `<RoomHeader items={modernHeaderItems}` を 1 行で固定するため改行しない. */}
          <RoomHeader items={modernHeaderItems} roomLabel={state.roomName || state.roomId.slice(0, 8)} ownerAuthenticated={isOwner} notice={MODERN_TEST_BUILD_NOTICE} layoutToggle={showToggle ? (
          <LayoutToggle
            current={layout}
            onChange={onLayoutChange}
            showOnboardingCoachmark={showCoachmark}
            onCoachmarkSeen={markCoachmarkSeen}
          />
        ) : undefined} retentionChip={<RoomRetentionChip roomId={state.roomId} adminToken={adminTokens.get(state.roomId) ?? undefined} onOpenSettings={() => setSettingsOpen(true)} />} />

          {/* 案B 骨格本体: 横並び flex。配置 (左 aside / canvas / 右 aside) は §9.10 構造化
            レイアウト state (workspace) から描く。mirror のとき order が反転し、左右の
            aside に入る region 群が入れ替わる (= 左右ミラー preset)。canvas は中央独立。

            aside の幅は「中にツールドックが入るか / 右ドックが入るか」で決める (= 移設前の
            左 244px / 右 260px を、固定スロットでなく **内容** に追従させる)。これで mirror
            しても各 region は自分に必要な幅のまま左右だけ入れ替わる。

            uiHidden (Tab toggle) のときは display:none で畳む (= 条件レンダーでなく
            className 切替。サイドバー内パネルの state/ref/store を再マウントで失わせない)。
            canvas は中央の flex-1 なので両側が消えた分さらに広がる。サイドバーの大きさ
            (sidebarScale) は CSS zoom で box ごと拡縮 (canvas には伝播しない)。 */}
          <div
            ref={layoutRowRef}
            className="relative flex min-h-0 flex-1 gap-2 p-2"
            // S1 (floating-panels-2026-08-01.md §9): ドロップ確定直後の互換 click (タッチの
            // ゴーストクリック) が着地点のパネル内ボタンを誤爆しないよう、確定直後の短時間
            // だけ次の 1 click を capture phase で飲み込む。armGhostClickGuard は
            // handlePanelDragEnd(true) からのみ立つ (= 通常のクリックは一切影響を受けない)。
            onClickCapture={(e) => {
              if (!ghostClickGuardArmedRef.current) return;
              ghostClickGuardArmedRef.current = false;
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {/* 第5スライス: レール DnD 中の 4 スロット ドロップ位置インジケータ。canvas を挟む
              4 隙間の縦線を出し、最寄り (railDropSlot) を強調する (= どこに入るか見える)。
              現在地 (railSlotNow) は薄く表示。row を anchor に absolute で配置 (leftOuter=左端 /
              rightOuter=右端 / leftInner=canvas 左隣 / rightInner=canvas 右隣)。 */}
            {railDragging && (
              <RailSlotIndicators
                activeSlot={railDropSlot}
                currentSlot={railSlotNow}
                geometry={railGeometry}
              />
            )}
            {/* S2 §11 エッジスナップの着地面バンド。collapse した region にも戻せる
              (region 矩形に依存しない画面端判定、§11 動機1)。着地先 region が解決できない
              (= 到達しないはずの防御) 場合は描かない。 */}
            {edgeSnapSide !== null &&
              (() => {
                const region = edgeSnapRegion(workspace, edgeSnapSide);
                if (region === null) return null;
                return (
                  <EdgeSnapBand side={edgeSnapSide} widthPx={asideWidthPx(workspace, [region])} />
                );
              })()}
            {/* 左 aside: order を canvas で分割した「左群」region を順に描く。幅は preset の
              ドック幅 (asideWidthPx、px) を inline style で与える (= 固定 Tailwind から
              データ駆動へ。preset/手動編集で幅が変わる土台)。aria-label は実内容から導出
              (mirror 後も中身と一致、§9.10 第2スライス a11y 修正)。全パネル非表示なら
              aside 自体を出さない。 */}
            {leftAsideVisible && (
              <aside
                ref={leftAsideRef}
                className={`${uiHidden ? 'hidden' : 'flex'} min-h-0 shrink-0 gap-2 overflow-y-auto ${asideFlexDir(
                  leftRegions,
                )}`}
                aria-label={leftAriaLabel}
                style={{
                  zoom: uiScaleToCssZoom(sidebarScale),
                  // rail-only aside は width 指定なし → 内容 hug (= レールがスリムのまま)。
                  ...(leftAsideWidth !== undefined ? { width: leftAsideWidth } : {}),
                }}
              >
                {leftRegions.map((region) => renderRegion(region))}
              </aside>
            )}

            {/* 左 aside ⇄ canvas 境界の splitter (§9.10 第3スライス)。drag で左ドック幅を更新
              (= live を custom 化、widths[asideWidthKey(leftRegions)])。サイドバー非表示
              (uiHidden / aside 非表示) のときは出さない。rail-only aside は固定幅を持たない
              ので splitter も出さない (= hug 幅をリサイズしない)。 */}
            {leftAsideVisible && !uiHidden && !leftRailOnly && (
              <ModernDockSplitter
                side="left"
                currentWidthPx={asideWidthPx(workspace, leftRegions)}
                asideRef={leftAsideRef}
                ariaLabel={`${leftAriaLabel} の幅を変更`}
                onResize={(w) => handleResizeAside(leftRegions, w)}
              />
            )}

            {/* 中: canvas 領域 = 空スロット div (§9.7 計測フローティング・スロット方式)。
              canvas 本体・overlay・scroll コンテナ・マスク枠は CanvasStage が描画し、
              RoomLayout 直下 absolute でこのスロット矩形に重なる。flex-1 で中央を最大化。 */}
            {panelRegistry.canvas()}

            {/* canvas ⇄ 右 aside 境界の splitter (§9.10 第3スライス)。drag で右ドック幅を更新。
              rail-only aside は固定幅を持たないので splitter を出さない (= hug 維持)。 */}
            {rightAsideVisible && !uiHidden && !rightRailOnly && (
              <ModernDockSplitter
                side="right"
                currentWidthPx={asideWidthPx(workspace, rightRegions)}
                asideRef={rightAsideRef}
                ariaLabel={`${rightAriaLabel} の幅を変更`}
                onResize={(w) => handleResizeAside(rightRegions, w)}
              />
            )}

            {/* 右 aside: order を canvas で分割した「右群」region を順に描く (mirror で
              ツール/プロパティ系が来ることもある)。幅 / aria-label / 出し分けは左 aside と同方針。 */}
            {rightAsideVisible && (
              <aside
                ref={rightAsideRef}
                className={`${uiHidden ? 'hidden' : 'flex'} min-h-0 shrink-0 gap-2 ${asideFlexDir(
                  rightRegions,
                )}`}
                aria-label={rightAriaLabel}
                style={{
                  zoom: uiScaleToCssZoom(sidebarScale),
                  // rail-only aside は width 指定なし → 内容 hug (= レールがスリムのまま)。
                  ...(rightAsideWidth !== undefined ? { width: rightAsideWidth } : {}),
                }}
              >
                {rightRegions.map((region) => renderRegion(region))}
              </aside>
            )}
          </div>

          {/* S2 (floating-panels-2026-08-01.md §2/§6/§9): フロート中パネルの層。
            `layoutRowRef` の外に置く (= ドック hit-test の候補集合 `[data-panel-id]` に
            混ざらない、フロートパネル自身がドロップ候補化しない)。position:fixed なので
            DOM 上の位置は見た目に影響しない。draggingId がフロート中パネルを指す間は
            handlePointerDragMove の判定結果 (dropTarget/floatPreview) をそのまま描画位置に
            反映する (= fully controlled、FloatShell 自身は座標 state を持たない、§2「同じ
            グリップ・同じ drag 経路」)。 */}
          {Object.entries(workspace.floating ?? {}).map(([rawId, pos]) => {
            const id = rawId as PanelId;
            if (pos === undefined || !isPanelVisible(workspace, id)) return null;
            const dragging = draggingId === id;
            // ドラッグ中かつフロート領域にいる間はポインタへ追従、ドック吸着圏内にいる間は
            // 直前のコミット済み位置に固定 (= 「ドックへ吸い込まれる直前」の視覚、FloatShell
            // の nearDock リングと対)。ドラッグしていない間は当然コミット済み位置そのまま。
            // clamp サイズは floatSizeFor (= 実測済みならそれを使う、バグ修正 2026-08-01
            // 「背の低いフロートが画面下まで移動できない」)。以前は常に
            // FLOAT_PANEL_FALLBACK_HEIGHT 固定だったため、実高さが小さいパネルでも
            // viewport 下端よりずっと手前で頭打ちになっていた。
            const position: FloatPoint =
              dragging && floatPreview !== null
                ? clampFloatPosition(
                    floatPositionFromPointer(
                      floatPreview.x,
                      floatPreview.y,
                      grabOffset ?? FLOAT_GRAB_OFFSET,
                    ),
                    floatSizeFor(id),
                    viewportSizeNow(),
                    FLOAT_EDGE_INSET_PX,
                  )
                : pos;
            // S2 §11 改訂 (consult 2026-08-01「⇤ドック → 位置ベース + 方向ラベル」):
            // 「フロート化した瞬間に居た region」を state に記憶する案 (floatOrigin) は
            // 撤回し、**現在の画面位置から都度導出**する (= 保持 state ゼロ)。着地先
            // region の決定はエッジスナップと同じ resolver (`edgeSnapRegion`) を共用する
            // (= 判定系を 2 つ作らない)。ラベル・aria-label・onReturnToDock はすべて同じ
            // `side`/`region` から導くので、矢印と実際の着地が食い違うことはない。
            const side = floatPanelSide(position, floatSizeFor(id), viewportSizeNow());
            // edgeSnapRegion が null を返すのは現行 4-region モデルでは到達しないはずの
            // 防御ケースのみ (§11 コメント参照)。それでも念のため homeRegionOf へ倒す。
            const region = edgeSnapRegion(workspace, side) ?? homeRegionOf(id);
            const returnToDockLabel = side === 'left' ? '←ドック' : 'ドック→';
            const returnToDockAriaLabel = `${PANEL_ARIA[id]} を${
              side === 'left' ? '左' : '右'
            }のドックへ戻す`;
            return (
              <FloatShell
                key={id}
                label={PANEL_ARIA[id]}
                position={position}
                zIndex={zIndexFromOrder(floatOrder, id)}
                isDragging={dragging}
                nearDock={dragging && dropTarget !== null}
                onPointerDragStart={(offset) => handlePanelDragStart(id, offset)}
                onPointerDragMove={handlePointerDragMove}
                onPointerDragEnd={handlePanelDragEnd}
                onRequestFront={() => setFloatOrder((order) => bringToFront(order, id))}
                onReturnToDock={() => {
                  // 「ドックへ戻す」導線 (§9 導線5)。上で導出した region (= 現在位置ベース)
                  // へ末尾挿入する。ラベルと同じ region を使うので矢印通りに着地する。
                  commitLive(movePanelLive(live, id, region, workspace.panels[region].length));
                }}
                returnToDockLabel={returnToDockLabel}
                returnToDockAriaLabel={returnToDockAriaLabel}
                onReposition={(next) => commitLive(floatPanelLive(live, id, next))}
                onMeasure={(size) => handleFloatMeasure(id, size)}
                widthPx={floatWidthFor(id)}
              >
                {panelRegistry[id]()}
              </FloatShell>
            );
          })}
          {/* §12 (floating-panels-2026-08-01.md、方針変更 2026-08-01): leftRail
            (toolRail+commandCluster) の一体フロート。旧 Java 版 `openToolFrame()`
            (Classic §12 と同根) を Modern の gateway 方針 (§1「CSP はツールバーも
            フロートできる」) に合わせて適用したもの。**1 ユニットの粒度**を守るため
            `toolRail`/`commandCluster` を個別の FloatShell にせず、1 個の FloatShell に
            両方を縦積みで入れる (= 上の通常パネル floating map とは別に単独描画)。
            grip / z-order / grab-offset 保持 / clamp は通常パネルと**完全に同じ共有基盤**
            (usePanelDragGrip 経由の FloatShell + floatShellGeometry) を再利用し、
            判定だけレール専用 (railDragging/railDropSlot、moveRailLive/floatRailLive) に
            分岐させる。 */}
          {isRailFloating(workspace) &&
            workspace.railFloating !== undefined &&
            (() => {
              const dragging = railDragging;
              const position: FloatPoint =
                dragging && floatPreview !== null
                  ? clampFloatPosition(
                      floatPositionFromPointer(
                        floatPreview.x,
                        floatPreview.y,
                        grabOffset ?? FLOAT_GRAB_OFFSET,
                      ),
                      floatSizeFor(RAIL_FLOAT_KEY),
                      viewportSizeNow(),
                      FLOAT_EDGE_INSET_PX,
                    )
                  : workspace.railFloating;
              // 位置ベース ⇤ドック (§11 と同じ心的モデル、レール版): 左に居れば
              // leftOuter、右に居れば rightOuter へ戻す (= 4 スロットの細かい狙いは
              // ドラッグでしか選べない、ボタンは「安全網」なので粗い 2 択で十分)。
              const side = floatPanelSide(
                position,
                floatSizeFor(RAIL_FLOAT_KEY),
                viewportSizeNow(),
              );
              const slot: RailSlot = side === 'left' ? 'leftOuter' : 'rightOuter';
              const returnToDockLabel = side === 'left' ? '←ドック' : 'ドック→';
              const returnToDockAriaLabel = `ツール帯を${side === 'left' ? '左' : '右'}のドックへ戻す`;
              return (
                <FloatShell
                  key="leftRail"
                  label="ツール帯"
                  position={position}
                  zIndex={zIndexFromOrder(floatOrder, RAIL_FLOAT_KEY)}
                  isDragging={dragging}
                  nearDock={dragging && railDropSlot !== null}
                  onPointerDragStart={(offset) => handlePanelDragStart(RAIL_FLOAT_KEY, offset)}
                  onPointerDragMove={handlePointerDragMove}
                  onPointerDragEnd={handlePanelDragEnd}
                  onRequestFront={() =>
                    setFloatOrder((order) => bringToFront(order, RAIL_FLOAT_KEY))
                  }
                  onReturnToDock={() => commitLive(moveRailLive(live, slot))}
                  returnToDockLabel={returnToDockLabel}
                  returnToDockAriaLabel={returnToDockAriaLabel}
                  onReposition={(next) => commitLive(floatRailLive(live, next))}
                  onMeasure={(size) => handleFloatMeasure(RAIL_FLOAT_KEY, size)}
                  widthPx={RAIL_FLOAT_WIDTH}
                >
                  {panelRegistry.toolRail()}
                  {panelRegistry.commandCluster()}
                </FloatShell>
              );
            })()}
          {/* detach 中のフロートプレビュー (consult 2026-08-01 追加依頼): ドック内パネルを
            掴んでフロート領域 (floatPreview!==null) に居る間、離すとどこにどのサイズで
            出るかをポインタに追従する半透明ゴーストで見せる。§3 実装方式 A1「外側では半透明の
            フロートプレビューがポインタに追従する」の未実装分 (= pointer drag へ全面移行した
            主目的の一つ)。**既にフロート中のパネルを動かしている場合は対象外**
            (= isPanelFloating で除外、その場合は本体 (FloatShell) 自身が既に追従描画して
            いるので二重に出さない)。エッジスナップのバンドとは floatPreview/edgeSnapSide が
            互いに排他な state 遷移なので自然に排他 (= 同時に両方出ない、handlePointerDragMove
            参照)。中身は実パネルを二重マウントするリスクを避け、枠 + タイトルのゴーストに
            留める (= 位置とサイズだけを実際の着地結果と一致させれば十分、というスコープ判断)。
            §12: leftRail が**既にフロート中**の reposition ドラッグでは実体 (FloatShell、
            上のブロック) が既に追従描画しているので、ここは対象外にする
            (`railDragging && isRailFloating(workspace)` を除外、通常パネルの
            `isPanelFloating` 除外と同じ役割)。leftRail が**まだ docked** から浮こうと
            している間は表示対象 (= 通常パネルの detach と同じ扱い)。 */}
          {draggingId !== null &&
            floatPreview !== null &&
            !isPanelFloating(workspace, draggingId) &&
            !(railDragging && isRailFloating(workspace)) &&
            (() => {
              // §12: レールのドックからの detach 中は、実際に掴んだのが toolRail /
              // commandCluster のどちらでも RAIL_FLOAT_KEY に正規化する (= 「レール全体」の
              // ゴーストとして一貫させる、floatSizeFor の lookup キーも揃える)。
              const id = railDragging ? RAIL_FLOAT_KEY : draggingId;
              const ghostSize = floatSizeFor(id);
              const ghostPos = clampFloatPosition(
                floatPositionFromPointer(
                  floatPreview.x,
                  floatPreview.y,
                  grabOffset ?? FLOAT_GRAB_OFFSET,
                ),
                ghostSize,
                viewportSizeNow(),
                FLOAT_EDGE_INSET_PX,
              );
              return (
                <div
                  aria-hidden="true"
                  data-testid="modern-float-detach-preview"
                  className="pointer-events-none fixed flex flex-col overflow-hidden rounded-lg border-2 border-dashed border-[var(--color-accent)] bg-[var(--color-panel)]/70 shadow-xl"
                  style={{
                    left: ghostPos.x,
                    top: ghostPos.y,
                    width: ghostSize.width,
                    height: ghostSize.height,
                    zIndex: FLOAT_DETACH_PREVIEW_Z,
                  }}
                >
                  <div className="flex items-center gap-1 rounded-t-lg bg-[var(--color-bg-soft)]/80 px-1 py-[2px] text-[10px] font-bold text-[var(--color-text-muted)]">
                    <span aria-hidden="true" className="opacity-60">
                      ⠿
                    </span>
                    <span className="truncate">{PANEL_ARIA[id]}</span>
                  </div>
                </div>
              );
            })()}
        </div>
      )}

      <WhatsNewDialog
        open={whatsNew.open}
        onClose={whatsNew.closeDialog}
        entries={whatsNew.entries}
        newIds={whatsNew.newIds}
      />
      <InviteDialog
        open={inviteDialog.open}
        onClose={inviteDialog.closeDialog}
        url={buildRoomInviteUrl(window.location.origin, state.roomId)}
        roomLabel={state.roomName || state.roomId.slice(0, 8)}
        entryPasswordRequired={inviteDialog.entryPasswordRequired}
        onAction={inviteDialog.report}
      />
      <ConfirmDialog
        open={clearConfirmOpen}
        title="キャンバスを全消し"
        message="キャンバスを全消ししますか?"
        confirmLabel="全消し"
        destructive
        onConfirm={() => {
          sendClear();
          setClearConfirmOpen(false);
        }}
        onCancel={() => setClearConfirmOpen(false)}
      />

      {/* §9.7: ImagepasteOverlay は CanvasStage 側へ移設 (canvas と一緒に永続化)。 */}

      {/* custom-cursor-2026-09-19.md §2.5: 「カーソル」エディタ。ModernSocialDock の
          [カーソル] ボタンから開く。中身は Classic とも共有する CursorEditorPanel
          (ModernCursorEditorPanel は薄いフロート chrome のみ)。 */}
      {cursorEditorOpen && (
        <ModernCursorEditorPanel
          onClose={() => setCursorEditorOpen(false)}
          initialCursor={selfCursor}
          currentColor={{ rgb24: chrome.pen.color, alpha: chrome.pen.alpha }}
          spuitMode={chrome.spuitMode}
          onSpuitPick={({ rgb24, alpha }) => {
            chrome.onColorChange(rgb24);
            chrome.onAlphaChange(alpha);
          }}
          onDraft={sendCursorDraft}
          onConfirm={sendCursorChange}
          onRemove={() => sendCursorChange(null)}
          // 作者 staging フィードバック 2026-09-19 follow-up: 自分の画面での表示を
          // 個人設定で止められる (PenSettingsPanel と同じ共有 chrome 値)。
          showOwnCustomCursor={chrome.showOwnCustomCursor}
          onShowOwnCustomCursorChange={chrome.onShowOwnCustomCursorChange}
        />
      )}

      {settingsOpen && (
        <RoomSettingsModal
          roomId={state.roomId}
          roomName={state.roomName}
          clearOpen={state.clearOpen}
          // close 経路で owner 判定を再評価 (= モーダル内でログインして token を
          // 取得した直後にボタンを認証済表示へ更新する)。
          onClose={closeSettings}
        />
      )}

      {/* 2026-06-12 共有ヘッダ「表示」: Classic と同じ UserPreferencesModal を配線。
          チャット文字サイズは Modern でも有効 (chatFontEm に反映)。サイドバーの大きさは
          Classic 固有のため Modern では視覚効果が出ない (= 値だけ永続化、後続でユーザが
          Modern 固有の「表示」設定の反映先を指定予定)。2026-06-13 続報: 旧「ペン詳細」
          セクションは独立した PenSettingsModal (下) へ移動した。 */}
      {uiPrefsOpen && (
        <UserPreferencesModal
          sidebarScale={sidebarScale}
          chatFontScale={chatFontScale}
          onSidebarScaleChange={setSidebarScale}
          onChatFontScaleChange={setChatFontScale}
          layerThumbnailsEnabled={chrome.layerThumbnailsEnabled}
          onLayerThumbnailsEnabledChange={chrome.onLayerThumbnailsEnabledChange}
          layerThumbnailsStopped={chrome.layerThumbnailsStopped}
          onClose={() => setUiPrefsOpen(false)}
        />
      )}

      {/* 2026-06-13 続報 (consult): ペン設定モーダル (手ぶれ補正 / AA)。Classic と同一の
          共有部品をヘッダ「ペン」項目から開く。値は共有 chrome の tri-state 実効値
          (共通既定 off) = ModernToolProperties のトグルと表示・実効値が常に一致する
          (同じ単一 source の別ビュー、二重の真実なし)。 */}
      {penSettingsOpen && (
        <PenSettingsModal
          stabilizerLevel={chrome.stabilizerLevel}
          onStabilizerChange={chrome.onStabilizerChange}
          aaEnabled={chrome.aaEnabled}
          onAaChange={chrome.onAaChange}
          dotByDotMode={chrome.dotByDotMode}
          onDotByDotModeChange={chrome.onDotByDotModeChange}
          dotByDotActive={chrome.dotByDotActive}
          // 2026-06-27 ブラシカーソル / スポイトの取得元 (共有 chrome): 旧「表示」モーダル
          // から本モーダルへ移設。Classic/Modern 共通の値。
          brushCursorType={chrome.brushCursorType}
          onBrushCursorTypeChange={chrome.onBrushCursorTypeChange}
          // 作者 staging フィードバック 2026-09-19 follow-up: 自分のカーソルを自分の
          // 画面にも表示するか (共有 chrome、CursorEditorPanel 下部と同じ値)。
          showOwnCustomCursor={chrome.showOwnCustomCursor}
          onShowOwnCustomCursorChange={chrome.onShowOwnCustomCursorChange}
          spuitSampleMode={chrome.spuitSampleMode}
          onSpuitSampleModeChange={chrome.onSpuitSampleModeChange}
          onClose={() => setPenSettingsOpen(false)}
        />
      )}

      {/* 2026-06-12 ショートカット設定 (shortcut-settings.md スライス2): Classic と同一の
          独立モーダル。`?` ホットキー / ヘッダ項目から開く。layout 非依存の共通部品。 */}
      {shortcutsOpen && (
        <ShortcutSettingsModal currentLayout="modern" onClose={() => setShortcutsOpen(false)} />
      )}

      {/* T3 タイムラプス X 共有 (growth-timelapse-share-2026-07-02.md §5.2/§5.3):
          編集1画面 → 進捗 → 共有ステップ。両シェル共通 (フォンはオーバーフローシート内の
          コマンドクラスタから開く)。canvas 寸法は roomMeta (state.canvas) 由来 = replay の
          原寸 raster 割当に必須 (T2 必須引数)。close で生成中 abort はモーダル側が担う。 */}
      {timelapseOpen && (
        <TimelapseShareModal
          roomId={state.roomId}
          canvasWidth={state.canvas.width}
          canvasHeight={state.canvas.height}
          transport={transport}
          workerFactory={createTimelapseWorker}
          isMobile={isMobile}
          adminToken={adminTokens.get(state.roomId) ?? undefined}
          onClose={() => setTimelapseOpen(false)}
        />
      )}

      {/* 過去キャンバス一覧 (Slice 2)。コマンドクラスタの「過去キャンバス」から開く。
          両シェル共通 (フォンはオーバーフローシート内のクラスタから)。絵ログ共有への
          橋渡し (snapshotSeq を終端に初期化) はモーダル内が担う。 */}
      {pastCanvasOpen && (
        <PastCanvasListModal
          roomId={state.roomId}
          canvasWidth={state.canvas.width}
          canvasHeight={state.canvas.height}
          transport={transport}
          workerFactory={createTimelapseWorker}
          isMobile={isMobile}
          adminToken={adminTokens.get(state.roomId) ?? undefined}
          permissions={state.permissions}
          getCurrentCanvasBlob={chrome.getCurrentCanvasBlob}
          currentBackgroundRgb={chrome.backgroundRgb}
          {...(pastCanvasRecoverSeq !== undefined
            ? { initialRecoverSeq: pastCanvasRecoverSeq }
            : {})}
          onClose={() => {
            setPastCanvasOpen(false);
            setPastCanvasRecoverSeq(undefined);
          }}
        />
      )}

      {/* A-4 背景色 ■ swatch (canvas-background-color §4.3): 背景色エディタ。
          §9.6 規律「Modern は Classic の floating ColorWheelPalette を import しない」に
          従い、Modern ネイティブの ModernColorPanel (= 描画色と同じカラーホイール軸) を
          軽量 floating コンテナに入れて出す。value=現在 bg seed、onColorChange=
          handleBackgroundColorChange (= 即ローカルプレビュー + trailing debounce send)。
          bg alpha は 255 固定 (ModernColorPanel は元々 alpha を発射しない)。perm が無く
          なったら閉じる。 */}
      {chrome.canSetBackground && bgColorWheelOpen && (
        <div
          data-testid="modern-bg-color-editor"
          className="fixed top-20 right-6 z-50 w-64 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-2 shadow-xl"
        >
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[11px] font-bold text-[var(--color-text-muted)]">背景色</span>
            <button
              type="button"
              aria-label="背景色エディタを閉じる"
              onClick={() => setBgColorWheelOpen(false)}
              className="flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg-soft)]"
            >
              ×
            </button>
          </div>
          <ModernColorPanel
            value={{ rgb24: chrome.backgroundRgb, alpha255: 255 }}
            onColorChange={chrome.handleBackgroundColorChange}
          />
        </div>
      )}

      {/* conflict-aware Undo (β Modern) の拒否トースト: reason 別の文言をキャンバス
          中央に約 3 秒表示する (undo-conflict-aware-2026-07-12.md「Undo 拒否時の UI —
          初期スコープに昇格」)。ImagepasteToastOverlay は canvasRef さえあれば
          position:fixed で正しく出るため、CanvasStage を介さずここで直接描ける
          (= 両シェル共通、フォン/デスクトップ分岐の外)。 */}
      <ImagepasteToastOverlay
        viewMapper={chrome.viewMapper}
        toast={undo.rejectToast}
        onDismiss={undo.dismissRejectToast}
        autoDismissMs={UNDO_REJECT_DISPLAY_MS}
      />

      {/* conflict-aware Undo の bbox ハイライト (undo-conflict-aware-2026-07-12.md
          「B' 実装での追加事項」の TODO を消化): `reason='conflict'` の交差 stroke を
          キャンバス上に一時的に赤枠で示す。トーストと同じ canvasRef 座標変換方式
          (= zoom/scroll は getBoundingClientRect が自動反映、flip だけ明示再マップ) で
          CanvasStage の外から直接描ける。キャンバス本体のピクセルには一切書き込まない
          (DOM overlay のみ)。 */}
      <UndoConflictHighlightOverlay viewMapper={chrome.viewMapper} boxes={undo.highlightBoxes} />

      {/* フィードバック浮きボタンはデスクトップのみ (= fixed right-4 bottom-4 が
          フォンシェルの下部バーと重なるため。フォンの導線は M2 で取捨)。 */}
      {!isMobile && <FeedbackButton />}
    </>
  );
};
